import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, parseISO, addDays, subDays, setDefaultOptions } from 'date-fns';
import { ar as dfAr, de as dfDe, nl as dfNl, fr as dfFr, es as dfEs } from 'date-fns/locale';
import i18nInstance from '../i18n';

const DF_LOCALES = { ar: dfAr, de: dfDe, nl: dfNl, fr: dfFr, es: dfEs };
const dfLocale = () => DF_LOCALES[(i18nInstance.language || 'en').split('-')[0]];
const calFormatters = {
  formatWeekdayName: (date) => {
    const loc = dfLocale();
    const isAr = (i18nInstance.language || '').split('-')[0] === 'ar';
    return format(date, isAr ? 'EEEEE' : 'EEEEEE', { locale: loc });
  },
};
import { axiosInstance, useAuth } from '../App';
import MultiDateBooking from './MultiDateBooking';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { toast } from 'sonner';
import {
  CalendarDays, CalendarRange, Search as SearchIcon, CalendarCheck, Plus, Pencil,
  CalendarClock, Ban, Trash2, RotateCcw, History, Download, ArrowLeft, Clock,
  Phone, Mail, User, Palette, Check, AlertTriangle,
  Users, ChevronDown, UserPlus, Crown, Shield, RotateCw, ListChecks, PartyPopper, Copy, MoreVertical, StickyNote, CalendarOff, ChevronLeft, ChevronRight, Bell,
} from 'lucide-react';
import ListsPanel from '../components/calendar/ListsPanel';
import NotesPanel from '../components/calendar/NotesPanel';
import { HOLIDAYS_BY_YEAR, AVAILABLE_HOLIDAY_YEARS } from '../data/holidays';

// Read-only holiday overlay data. Only National (🟡) + Andalucía (🟢) holidays
// are overlaid on the calendar because they apply everywhere; Local (🔴)
// holidays are municipality-specific and a calendar has no reliable municipality
// (see Holidays page for town-specific local holidays). Keyed by 'YYYY-MM-DD'.
const HOLIDAY_SCOPE_META = {
  national:  { dot: '#facc15', label: 'National' },   // yellow
  andalusia: { dot: '#22c55e', label: 'Andalucía' },  // green
  local:     { dot: '#ef4444', label: 'Local' },      // red (town-specific)
};
const UNIVERSAL_HOLIDAYS_BY_DATE = (() => {
  const map = {};
  for (const y of AVAILABLE_HOLIDAY_YEARS) {
    for (const h of (HOLIDAY_BY_YEAR_SAFE(y))) {
      if (h.scope === 'national' || h.scope === 'andalusia') {
        (map[h.date] = map[h.date] || []).push(h);
      }
    }
  }
  return map;
})();
// Local holidays indexed by date; each carries its `town` so the overlay can
// match ONLY the calendar's own municipality (never another town's).
const LOCAL_HOLIDAYS_BY_DATE = (() => {
  const map = {};
  for (const y of AVAILABLE_HOLIDAY_YEARS) {
    for (const h of (HOLIDAY_BY_YEAR_SAFE(y))) {
      if (h.scope === 'local' && h.town) {
        (map[h.date] = map[h.date] || []).push(h);
      }
    }
  }
  return map;
})();
function HOLIDAY_BY_YEAR_SAFE(y) { return HOLIDAYS_BY_YEAR[y] || []; }

// Fixed professional palette (20 distinct colours) — shared across the calendar.
const PALETTE = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
  '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#b45309',
  '#1e3a8a', '#64748b',
];
const NO_CAT_DOT = '#94a3b8';

const STATUS_OPTIONS = ['confirmed', 'tentative', 'cancelled', 'completed', 'no_show'];
const STATUS_LABEL_KEYS = {
  confirmed: 'bookingCal.st_confirmed', tentative: 'bookingCal.st_tentative',
  cancelled: 'bookingCal.st_cancelled', completed: 'bookingCal.st_completed', no_show: 'bookingCal.st_no_show',
};
const STATUS_COLORS = {
  confirmed: 'bg-emerald-100 text-emerald-700',
  tentative: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700',
  completed: 'bg-gray-100 text-gray-600',
  no_show: 'bg-orange-100 text-orange-700',
};
const todayStr = () => format(new Date(), 'yyyy-MM-dd');
const emptyForm = () => ({
  date: todayStr(), startTime: '09:00', endTime: '10:00',
  clientName: '', service: '', phone: '', email: '', notes: '', status: 'confirmed', categoryId: '', assigneeUserId: '',
  repeat: 'none', customFreq: 'weekly', customInterval: 1, customWeekdays: [],
  endType: 'count', endCount: 10, endDate: '',
  payStatus: 'unpaid', payPrice: '', payAmount: '', payMethod: 'not_specified', payNote: '',
  reminderOffsets: [],
});

const REMINDER_OPTIONS = [
  { value: 15, key: 'remind15', label: '15 minutes before' },
  { value: 60, key: 'remind60', label: '1 hour before' },
  { value: 1440, key: 'remind1d', label: '1 day before' },
];

// Safely turn any API error (incl. FastAPI 422 detail arrays/objects) into a readable string.
const NOTES_MAX = 10000;
const FIELD_LABELS = { notes: 'Notes', clientName: 'Client name', service: 'Service', phone: 'Phone', email: 'Email' };
const errMsg = (e, fallback = 'Error') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    // Prefer a friendly, field-specific message for length errors instead of
    // the raw backend text (e.g. "Notes are too long. Maximum 4,000 characters.").
    for (const x of d) {
      const isLen = x && typeof x === 'object' && (x.type === 'string_too_long' || /at most/i.test(x.msg || ''));
      if (isLen) {
        const field = Array.isArray(x.loc) ? x.loc[x.loc.length - 1] : '';
        const max = x.ctx?.max_length;
        const label = FIELD_LABELS[field] || 'This field';
        if (max) return `${label} ${label === 'Notes' ? 'are' : 'is'} too long. Maximum ${Number(max).toLocaleString()} characters.`;
      }
    }
    return d.map(x => (x && typeof x === 'object' ? x.msg : x)).filter(Boolean).join('; ') || fallback;
  }
  if (d && typeof d === 'object') return d.msg || fallback;
  return fallback;
};

// Auto-grow a textarea to fit its content (mobile-friendly), capped so the
// dialog stays usable. Desktop users can still drag-resize via `resize-y`.
const autoGrowNotes = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 400) + 'px';
};

// Fixed dropdown time options so the Start/End selectors look and behave the
// SAME on every device / browser / installed PWA. Native <input type="time">
// renders a clock icon on some Androids and a dropdown on others; a native
// <select> (the app's standard dropdown, as used for the municipality picker)
// is consistent everywhere. 5-minute steps; the current stored value is always
// included even if it is off-grid, so existing booking times preselect exactly.
const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 5) {
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return out;
})();

const TimeSelect = ({ value, onChange, 'data-testid': testId }) => {
  const opts = value && !TIME_OPTIONS.includes(value) ? [value, ...TIME_OPTIONS] : TIME_OPTIONS;
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      data-testid={testId}
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {!value && <option value="" disabled>--:--</option>}
      {opts.map(tk => <option key={tk} value={tk}>{tk}</option>)}
    </select>
  );
};

// Consistent date picker (react-day-picker Calendar in a Popover) so the Date
// field opens the SAME calendar selector on every device / browser / PWA rather
// than the device's native date input. The current value preselects and the
// month opens on it. Popover renders inside the Dialog tree so its focus works.
const DateSelect = ({ value, onChange, placeholder, displayFormat = 'EEE, d MMM yyyy', 'data-testid': testId }) => {
  const [open, setOpen] = React.useState(false);
  const selected = value ? parseISO(value) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <span className={`truncate ${value ? '' : 'text-muted-foreground'}`}>
            {value ? format(selected, displayFormat) : (placeholder || 'Pick a date')}
          </span>
          <CalendarDays className="h-4 w-4 opacity-60 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[60]" align="start" data-testid={`${testId}-panel`}>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          weekStartsOn={1}
          onSelect={(d) => { if (d) { onChange(format(d, 'yyyy-MM-dd')); setOpen(false); } }}
          initialFocus
          locale={dfLocale()}
          dir={i18nInstance.dir()}
          formatters={calFormatters}
        />
      </PopoverContent>
    </Popover>
  );
};

// ---------------------------------------------------------------------------
// Booking list building blocks — HOISTED to module scope on purpose.
// They were previously declared inside CalendarPage's render, which gave them a
// NEW component identity on every render, so any state change (e.g. tapping the
// ⋮ actions menu) remounted the entire booking list and reset the scroll
// position to the top. Hoisting keeps their type STABLE so React reconciles in
// place and the scroll position is preserved. Everything they need is passed in
// via `ctx`. The ⋮ actions-open state is LOCAL to each card, so opening/closing
// it never re-renders the parent list at all.
// ---------------------------------------------------------------------------
const BirthdayChips = ({ dateStr, ctx }) => {
  const { birthdaysForDate, t } = ctx;
  const list = birthdaysForDate(dateStr);
  if (!list.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1 items-center" data-testid={`calendar-birthday-${dateStr}`}>
      {list.map(bd => (
        <span key={bd.id} className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-700" title={t('bookingCal.birthday', 'Birthday')} data-testid={`calendar-birthday-chip-${bd.id}`}>
          🎂 {bd.personName}
        </span>
      ))}
    </span>
  );
};

const HolidayChips = ({ dateStr, ctx }) => {
  const { holidaysForDate, holidayName } = ctx;
  const hols = holidaysForDate(dateStr);
  if (!hols || hols.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap gap-1 items-center" data-testid={`calendar-holiday-${dateStr}`}>
      {hols.map((h, i) => {
        const meta = HOLIDAY_SCOPE_META[h.scope] || HOLIDAY_SCOPE_META.national;
        const label = h.scope === 'local' && h.town ? `${holidayName(h)} — ${h.town}` : holidayName(h);
        return (
          <span
            key={i}
            className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
            style={{ backgroundColor: `${meta.dot}22`, color: '#374151' }}
            title={label}
            data-testid={`calendar-holiday-chip-${dateStr}-${h.scope}`}
          >
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: meta.dot }} />
            {label}
          </span>
        );
      })}
    </span>
  );
};

const BookingCard = ({ b, ctx }) => {
  const { catMap, canWrite, canDelete, canDeleteBooking, t, paymentBadge, describeRecurrence,
          openEdit, openReschedule, openDuplicate, openDelete, openPurge, openAudit,
          setCancelScope, setConfirmCancel, setConfirmSkip } = ctx;
  const cat = b.categoryId ? catMap[b.categoryId] : null;
  const cancelled = b.status === 'cancelled';
  const [actionsOpen, setActionsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const notesLines = (b.notes || '').split('\n');
  const firstNoteLine = notesLines.find(l => l.trim() !== '') || '';
  const hasMoreNotes = (b.notes || '').trim() !== '' && (b.notes || '').trim() !== firstNoteLine.trim();
  const cardStyle = cancelled
    ? { backgroundColor: '#f3f4f6', borderLeftColor: '#9ca3af' }
    : { backgroundColor: cat ? `${cat.color}14` : '#ffffff', borderLeftColor: cat?.color || '#e5e7eb' };
  return (
  <div className={`rounded-lg border p-3 border-l-4 ${cancelled ? 'opacity-70' : ''}`} style={cardStyle} data-testid={`booking-${b.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-semibold flex items-center gap-1 ${cancelled ? 'line-through text-gray-500' : 'text-gray-900'}`}><Clock className="h-3.5 w-3.5 text-gray-400" />{b.startTime}–{b.endTime}</span>
          <Badge className={`${STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-600'} border-0 text-[10px]`} data-testid={`booking-status-${b.id}`}>{t(STATUS_LABEL_KEYS[b.status] || '', b.status)}</Badge>
          {b.seriesId && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-900/5 text-gray-500" title={t('bookingCal.repeats', 'Repeating booking')} data-testid={`booking-repeat-${b.id}`}>
              <RotateCw className="h-2.5 w-2.5" />{t('bookingCal.repeatsShort', 'Repeats')}
            </span>
          )}
          {cat && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${cat.color}22`, color: cat.color }} data-testid={`booking-category-${b.id}`}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />{cat.name}
            </span>
          )}
          {(() => { const pb = paymentBadge(b); return pb ? (
            <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${pb.cls}`} data-testid={`booking-payment-${b.id}`}>{pb.label}</span>
          ) : null; })()}
          {Array.isArray(b.reminders) && b.reminders.length > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700" title={t('bookingCal.reminderSet', 'Reminder set')} data-testid={`booking-reminder-${b.id}`}>
              <CalendarClock className="h-2.5 w-2.5" />{b.reminders.length}
            </span>
          )}
          {b.assigneeName && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700" title={t('bookingCal.md_assignee', 'Assigned to')} data-testid={`booking-assignee-${b.id}`}>
              <User className="h-2.5 w-2.5" />{b.assigneeName}
            </span>
          )}
        </div>
        <p className={`text-sm font-medium mt-1 flex items-center gap-1 ${cancelled ? 'line-through text-gray-500' : 'text-gray-900'}`}><User className="h-3.5 w-3.5 text-gray-400" />{b.clientName}</p>
        {b.service && <p className="text-[13px] text-gray-600">{b.service}</p>}
        {b.seriesId && b.recurrence && (
          <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5" data-testid={`booking-repeat-summary-${b.id}`}>
            <RotateCw className="h-3 w-3 text-gray-400" />{describeRecurrence(b.recurrence)}
          </p>
        )}
        <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500 flex-wrap">
          {b.phone && <span className="flex items-center gap-0.5"><Phone className="h-3 w-3" />{b.phone}</span>}
          {b.email && <span className="flex items-center gap-0.5"><Mail className="h-3 w-3" />{b.email}</span>}
        </div>
        {b.notes && (
          <div className="mt-1">
            {hasMoreNotes ? (
              <button
                type="button"
                onClick={() => setNotesOpen(o => !o)}
                className="flex items-start gap-1 text-left text-[12px] text-gray-500 italic hover:text-gray-700 transition-colors w-full"
                data-testid={`booking-notes-toggle-${b.id}`}
              >
                <ChevronDown className={`h-3.5 w-3.5 mt-0.5 shrink-0 transition-transform ${notesOpen ? 'rotate-180' : ''}`} />
                <span className={notesOpen ? '' : 'truncate'} data-testid={`booking-notes-summary-${b.id}`}>{firstNoteLine}</span>
              </button>
            ) : (
              <p className="text-[12px] text-gray-500 italic" data-testid={`booking-notes-summary-${b.id}`}>{firstNoteLine}</p>
            )}
            {hasMoreNotes && notesOpen && (
              <p className="text-[12px] text-gray-500 mt-1 whitespace-pre-wrap" data-testid={`booking-notes-full-${b.id}`}>{b.notes}</p>
            )}
          </div>
        )}
      </div>
      <Button
        size="icon"
        variant="ghost"
        className={`h-8 w-8 shrink-0 rounded-full text-gray-500 hover:bg-black/5 ${actionsOpen ? 'bg-black/5 text-gray-900' : ''}`}
        onClick={() => setActionsOpen(o => !o)}
        aria-label={t('bookingCal.actions', 'Actions')}
        aria-expanded={actionsOpen}
        data-testid={`booking-actions-toggle-${b.id}`}
      >
        <MoreVertical className="h-4 w-4" />
      </Button>
    </div>
    {actionsOpen && (
    <div className="flex items-center gap-1 mt-2 flex-wrap border-t border-black/5 pt-2" data-testid={`booking-actions-${b.id}`}>
      {canWrite && <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px]" onClick={() => openEdit(b)} data-testid={`booking-edit-${b.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />{t('bookingCal.edit', 'Edit')}</Button>}
      {canWrite && <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px]" onClick={() => openReschedule(b)} data-testid={`booking-reschedule-${b.id}`}><CalendarClock className="h-3.5 w-3.5 mr-1" />{t('bookingCal.reschedule', 'Reschedule')}</Button>}
      {canWrite && b.status !== 'cancelled' && <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px] text-blue-600" onClick={() => openDuplicate(b)} data-testid={`booking-duplicate-${b.id}`}><Copy className="h-3.5 w-3.5 mr-1" />{t('bookingCal.duplicate', 'Duplicate')}</Button>}
      {canWrite && b.status !== 'cancelled' && <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px] text-orange-600" onClick={() => { setCancelScope('this'); setConfirmCancel(b); }} data-testid={`booking-cancel-${b.id}`}><Ban className="h-3.5 w-3.5 mr-1" />{t('bookingCal.cancel', 'Cancel')}</Button>}
      {canDeleteBooking(b) && b.seriesId && b.status !== 'cancelled' && <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px] text-amber-600" onClick={() => setConfirmSkip(b)} data-testid={`booking-skip-${b.id}`}><CalendarOff className="h-3.5 w-3.5 mr-1" />{t('bookingCal.skipDate', 'Skip this date')}</Button>}
      {canDeleteBooking(b) && <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px] text-red-600" onClick={() => openDelete(b)} data-testid={`booking-delete-${b.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />{t('bookingCal.delete', 'Delete')}</Button>}
      {canDeleteBooking(b) && <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px] text-red-700 font-semibold" onClick={() => openPurge(b)} data-testid={`booking-purge-${b.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />{t('bookingCal.deletePermanently', 'Delete permanently')}</Button>}
      <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px] text-gray-500" onClick={() => openAudit(b)} data-testid={`booking-history-${b.id}`}><History className="h-3.5 w-3.5 mr-1" />{t('bookingCal.history', 'History')}</Button>
    </div>
    )}
  </div>
  );
};

const DateGroup = ({ date, items, ctx }) => (
  <div data-testid={`calendar-day-${date}`}>
    <div className="sticky top-0 bg-slate-50 py-1 z-10">
      <h2 className="text-sm font-bold text-gray-700">{format(parseISO(date), 'EEEE, d MMMM yyyy')}</h2>
      <HolidayChips dateStr={date} ctx={ctx} />
      <BirthdayChips dateStr={date} ctx={ctx} />
    </div>
    <div className="space-y-2 mt-1">{items.map(b => <BookingCard key={b.id} b={b} ctx={ctx} />)}</div>
  </div>
);


const CalendarPage = () => {
  const { t, i18n } = useTranslation();
  setDefaultOptions({ locale: dfLocale() });
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [calendar, setCalendar] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [recycle, setRecycle] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catsLoaded, setCatsLoaded] = useState(false);
  const [birthdays, setBirthdays] = useState([]);
  const [bdayOpen, setBdayOpen] = useState(false);
  const [bdayEditing, setBdayEditing] = useState(null);
  const [bdayForm, setBdayForm] = useState({ personName: '', day: '1', month: '1', reminder: 'none' });
  const [bdayToDelete, setBdayToDelete] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [payFilter, setPayFilter] = useState('all');

  // Manage-categories dialog
  const [manageCatOpen, setManageCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState(PALETTE[4]);
  const [catToDelete, setCatToDelete] = useState(null);

  const [mode, setMode] = useState('calendar'); // calendar | agenda | search | recycle
  const [month, setMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [agendaFilter, setAgendaFilter] = useState('today'); // today | upcoming | past | cancelled
  const [query, setQuery] = useState('');

  // dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [reForm, setReForm] = useState({ date: '', startTime: '', endTime: '' });
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmSkip, setConfirmSkip] = useState(null);
  const [editScope, setEditScope] = useState('this');
  const [dupEditOpen, setDupEditOpen] = useState(false);
  const [cancelScope, setCancelScope] = useState('this');
  const [deleteScope, setDeleteScope] = useState('this');
  const [confirmPurge, setConfirmPurge] = useState(null);
  const [purgeScope, setPurgeScope] = useState('this');
  const [multiDateOpen, setMultiDateOpen] = useState(false);
  const [auditFor, setAuditFor] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [backupStatus, setBackupStatus] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exporting, setExporting] = useState(false);
  // Self-service calendars
  const [allCalendars, setAllCalendars] = useState([]);
  const [deletedCalendars, setDeletedCalendars] = useState([]);
  const [selectedCalId, setSelectedCalId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newCalName, setNewCalName] = useState('');
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameVal, setRenameVal] = useState('');
  const [confirmDeleteCal, setConfirmDeleteCal] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [busyMembers, setBusyMembers] = useState(false);
  const [confirmRemoveMember, setConfirmRemoveMember] = useState(null);
  // Duplicate booking (independent copies — never a recurring series)
  const [dupOpen, setDupOpen] = useState(false);
  const [dupSource, setDupSource] = useState(null);
  const [dupDates, setDupDates] = useState([]);
  const [dupMode, setDupMode] = useState('pick');       // 'pick' | 'range'
  const [dupRangeFrom, setDupRangeFrom] = useState('');
  const [dupRangeTo, setDupRangeTo] = useState('');
  const [dupStart, setDupStart] = useState('09:00');
  const [dupEnd, setDupEnd] = useState('10:00');
  const [dupCopyReminders, setDupCopyReminders] = useState(false);
  const [dupBusy, setDupBusy] = useState(false);
  const [dupGroupId, setDupGroupId] = useState('');
  // Group-delete for duplicated bookings
  const [dupDelFor, setDupDelFor] = useState(null);        // the booking whose Delete was pressed
  const [dupDelMode, setDupDelMode] = useState('menu');     // 'menu' | 'select' | 'confirmAll'
  const [dupDelSel, setDupDelSel] = useState(() => new Set());
  const [dupDelBusy, setDupDelBusy] = useState(false);
  const [townOptions, setTownOptions] = useState([]);
  const [newCalMunicipality, setNewCalMunicipality] = useState('');
  const [renameMunicipality, setRenameMunicipality] = useState('');

  const role = calendar?.myRole;
  const canWrite = ['owner', 'admin', 'member'].includes(role);
  const canDelete = ['owner', 'admin'].includes(role);
  // Per-booking delete rights: owner/admin may delete any booking; a plain member
  // may delete/restore ONLY bookings they personally created. Enforced on the
  // server too — this just mirrors it so members don't see buttons that 403.
  const myUserId = user?.userId || user?.id;
  const canDeleteBooking = (b) => canDelete || (role === 'member' && !!b?.createdBy && b.createdBy === myUserId);

  // Per-member, per-calendar opt-in for shared-calendar change notifications.
  const [notifyOnChange, setNotifyOnChange] = useState(false);
  const [savingNotify, setSavingNotify] = useState(false);
  useEffect(() => { setNotifyOnChange(!!calendar?.myNotifyOnChange); }, [calendar?.id, calendar?.myNotifyOnChange]);
  const toggleNotify = async () => {
    if (!calendar?.id || savingNotify) return;
    const next = !notifyOnChange;
    setSavingNotify(true);
    setNotifyOnChange(next);
    try {
      await axiosInstance.put(`/calendar/calendars/${calendar.id}/notify-pref`, { notifyOnChange: next });
      setAllCalendars(prev => prev.map(c => c.id === calendar.id ? { ...c, myNotifyOnChange: next } : c));
      setCalendar(prev => (prev ? { ...prev, myNotifyOnChange: next } : prev));
      toast.success(next
        ? t('bookingCal.notifyOn', 'You will be notified when bookings change')
        : t('bookingCal.notifyOff', 'Change notifications turned off'));
    } catch (e) {
      setNotifyOnChange(!next);
      toast.error(t('bookingCal.notifySaveError', 'Could not save your notification choice. Please try again.'));
    } finally { setSavingNotify(false); }
  };

  const loadCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/calendar/calendars');
      const cals = r.data.calendars || [];
      const savedLastId = r.data.lastCalendarId || null;
      setAllCalendars(cals);
      setDeletedCalendars(r.data.deletedCalendars || []);
      setSelectedCalId(prev => {
        const qpCal = new URLSearchParams(window.location.search).get('calendarId');
        const qpMatch = qpCal && cals.find(c => c.id === qpCal);
        const savedMatch = savedLastId && cals.find(c => c.id === savedLastId);
        const keep = prev && cals.find(c => c.id === prev);
        const chosen = qpMatch ? cals.find(c => c.id === qpCal)
          : savedMatch ? cals.find(c => c.id === savedLastId)
          : keep ? cals.find(c => c.id === prev)
          : (cals.find(c => c.name === 'Maradels Client Bookings') || cals[0] || null);
        setCalendar(chosen || null);
        return chosen ? chosen.id : null;
      });
    } catch (e) {
      setCalendar(null); setAllCalendars([]); setDeletedCalendars([]);
      if (e?.response?.status && ![401, 403].includes(e.response.status)) {
        toast.error(t('bookingCal.loadError', 'Could not load your calendar. Please try again.'));
      }
    } finally { setLoading(false); }
  }, [t]);

  const selectCalendar = (cal) => {
    setSelectedCalId(cal.id);
    setCalendar(cal);
    setMode('calendar');
    setCategoryFilter('all');
    // Remember this choice on the account so it follows the user across devices
    // and survives logout. Best-effort: a failure never blocks the selection.
    axiosInstance.put('/calendar/me/last-calendar', { calendarId: cal.id }).catch(() => {});
  };

  // Deep-link: /calendar?view=lists opens the existing Lists tab once the
  // calendar is ready (consumed once so tab switching still works normally).
  const listsDeepLinkRef = useRef(new URLSearchParams(window.location.search).get('view') === 'lists');
  useEffect(() => {
    if (calendar?.id && listsDeepLinkRef.current) {
      listsDeepLinkRef.current = false;
      setMode('lists');
    }
  }, [calendar]);

  const loadBookings = useCallback(async (calId) => {
    if (!calId) return;
    try {
      const r = await axiosInstance.get(`/calendar/calendars/${calId}/bookings`);
      setBookings(r.data.bookings || []);
    } catch { /* empty state handles */ }
  }, []);

  const loadRecycle = useCallback(async (calId) => {
    if (!calId) return;
    try {
      const r = await axiosInstance.get(`/calendar/calendars/${calId}/recycle-bin`);
      setRecycle(r.data.bookings || []);
    } catch { /* noop */ }
  }, []);

  const loadCategories = useCallback(async (calId) => {
    if (!calId) return;
    try {
      const r = await axiosInstance.get(`/calendar/calendars/${calId}/categories`);
      setCategories(r.data.categories || []);
    } catch { /* noop */ }
    finally { setCatsLoaded(true); }
  }, []);

  const loadBirthdays = useCallback(async (calId) => {
    if (!calId) return;
    try {
      const r = await axiosInstance.get(`/calendar/calendars/${calId}/birthdays`);
      setBirthdays(r.data.birthdays || []);
    } catch { /* noop */ }
  }, []);

  const loadBackupStatus = useCallback(async (calId, canSee) => {
    if (!calId || !canSee) { setBackupStatus(null); return; }
    try {
      const r = await axiosInstance.get(`/calendar/calendars/${calId}/backup-status`);
      setBackupStatus(r.data || null);
    } catch { setBackupStatus(null); }
  }, []);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);
  // Canonical Almería town list for the calendar Municipality selector (reused
  // from the existing locations endpoint — no separate town list is created).
  useEffect(() => {
    let ok = true;
    axiosInstance.get('/locations/almeria/towns')
      .then(r => { if (ok) setTownOptions((r.data?.towns || []).map(x => x.name)); })
      .catch(() => { /* selector still works, just empty */ });
    return () => { ok = false; };
  }, []);
  useEffect(() => { if (calendar?.id) loadBookings(calendar.id); }, [calendar?.id, loadBookings]);
  // Deep-link from a change notification: /calendar?calendarId=X&bookingId=Y —
  // jump to that booking's day, scroll to it and briefly highlight it.
  const deepLinkBookingRef = useRef(new URLSearchParams(window.location.search).get('bookingId'));
  useEffect(() => {
    const bid = deepLinkBookingRef.current;
    if (!bid || !calendar?.id) return;
    const target = bookings.find(b => b.id === bid);
    if (!target) return; // bookings not loaded yet (or not on this calendar)
    deepLinkBookingRef.current = null; // consume once
    try { const u = new URL(window.location.href); u.searchParams.delete('bookingId'); window.history.replaceState({}, '', u); } catch (e) {}
    setMode('calendar');
    try { const [y, m, d] = target.date.split('-').map(Number); setSelectedDate(new Date(y, m - 1, d)); } catch (e) {}
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="booking-${bid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'box-shadow 0.3s ease';
        el.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.9)';
        el.style.borderRadius = '0.5rem';
        setTimeout(() => { el.style.boxShadow = ''; }, 2800);
      }
    }, 600);
  }, [bookings, calendar?.id]);
  useEffect(() => { if (calendar?.id) loadBirthdays(calendar.id); }, [calendar?.id, loadBirthdays]);
  useEffect(() => { if (calendar?.id) { setCatsLoaded(false); loadCategories(calendar.id); } }, [calendar?.id, loadCategories]);
  // Auto-seed a small renameable starter set when an OWNER opens a calendar that
  // has no categories yet (so the Category/colour dropdown is never empty).
  const seededRef = useRef({});
  useEffect(() => {
    if (!calendar?.id || role !== 'owner' || !catsLoaded) return;
    if (categories.length === 0 && !seededRef.current[calendar.id]) {
      seededRef.current[calendar.id] = true;
      (async () => {
        try {
          await axiosInstance.post(`/calendar/calendars/${calendar.id}/categories/seed-starter`);
          await loadCategories(calendar.id);
        } catch { /* noop */ }
      })();
    }
  }, [calendar?.id, role, catsLoaded, categories.length, loadCategories]);
  useEffect(() => { if (calendar?.id) loadBackupStatus(calendar.id, ['owner', 'admin'].includes(calendar?.myRole)); }, [calendar?.id, calendar?.myRole, loadBackupStatus]);
  useEffect(() => { if (mode === 'recycle' && calendar?.id) loadRecycle(calendar.id); }, [mode, calendar?.id, loadRecycle]);

  const catMap = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c])), [categories]);
  const usedColors = useMemo(() => new Set(categories.map(c => c.color)), [categories]);
  // When opening Manage Categories, default the new-category swatch to a free colour.
  useEffect(() => {
    if (manageCatOpen && usedColors.has(newCatColor)) {
      const avail = PALETTE.find(c => !usedColors.has(c));
      if (avail) setNewCatColor(avail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manageCatOpen, usedColors]);
  const dotsByDate = useMemo(() => {
    const m = {};
    bookings.filter(b => b.status !== 'cancelled').forEach(b => {
      const key = b.categoryId || '__none__';
      const color = (b.categoryId && catMap[b.categoryId]?.color) || NO_CAT_DOT;
      const arr = m[b.date] = m[b.date] || [];
      if (!arr.some(d => d.key === key)) arr.push({ key, color });
    });
    return m;
  }, [bookings, catMap]);
  const applyCatFilter = useCallback((list) => {
    let l = list;
    if (categoryFilter === '__none__') l = l.filter(b => !b.categoryId);
    else if (categoryFilter !== 'all') l = l.filter(b => (b.categoryId || '') === categoryFilter);
    if (payFilter !== 'all') {
      const kind = (b) => {
        const p = b.payment; if (!p) return 'none';
        if (p.paymentStatus === 'free') return 'free';
        return ((p.priceCents || 0) - (p.amountPaidCents || 0)) > 0 ? 'due' : 'paid';
      };
      l = l.filter(b => kind(b) === payFilter);
    }
    return l;
  }, [categoryFilter, payFilter]);
  // Reset a stale filter if its category was deleted (by this user or the other).
  useEffect(() => {
    if (categoryFilter !== 'all' && categoryFilter !== '__none__' && !catMap[categoryFilter]) {
      setCategoryFilter('all');
    }
  }, [catMap, categoryFilter]);

  const groupByDate = (list) => {
    const s = [...list].sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime));
    const m = {};
    s.forEach(b => { (m[b.date] = m[b.date] || []).push(b); });
    return Object.entries(m);
  };

  const selStr = format(selectedDate, 'yyyy-MM-dd');
  const dayList = useMemo(() =>
    applyCatFilter(bookings.filter(b => b.date === selStr)).sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [bookings, selStr, applyCatFilter]);

  const agendaGroups = useMemo(() => {
    const td = todayStr();
    let f = [];
    if (agendaFilter === 'today') f = bookings.filter(b => b.date === td);
    else if (agendaFilter === 'upcoming') f = bookings.filter(b => b.date >= td && b.status !== 'cancelled');
    else if (agendaFilter === 'past') f = bookings.filter(b => b.date < td);
    else if (agendaFilter === 'cancelled') f = bookings.filter(b => b.status === 'cancelled');
    return groupByDate(applyCatFilter(f));
  }, [bookings, agendaFilter, applyCatFilter]);

  const searchGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const f = bookings.filter(b =>
      (b.clientName || '').toLowerCase().includes(q) || (b.service || '').toLowerCase().includes(q));
    return groupByDate(applyCatFilter(f));
  }, [bookings, query, applyCatFilter]);

  // Birthdays are a separate yearly type — make them searchable by person name
  // in the SAME Search screen (reuses the existing birthday system/records).
  const searchBirthdays = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return birthdays.filter(bd => (bd.personName || '').toLowerCase().includes(q));
  }, [birthdays, query]);

  // ---- actions (unchanged Phase 1A logic) ----
  const openCreate = (dateStr) => { setEditing(null); setForm({ ...emptyForm(), date: dateStr || selStr }); setFormOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setEditScope('this');
    const p = b.payment || null;
    setForm({ ...emptyForm(), date: b.date, startTime: b.startTime, endTime: b.endTime, clientName: b.clientName,
      service: b.service || '', phone: b.phone || '', email: b.email || '', notes: b.notes || '',
      status: b.status, categoryId: b.categoryId || '', assigneeUserId: b.assigneeUserId || '',
      payStatus: p?.paymentStatus || 'unpaid',
      payPrice: p && p.priceCents != null ? (p.priceCents / 100).toFixed(2) : '',
      payAmount: p && p.amountPaidCents ? (p.amountPaidCents / 100).toFixed(2) : '',
      payMethod: p?.paymentMethod || 'not_specified',
      payNote: p?.paymentNote || '',
      reminderOffsets: Array.isArray(b.reminders) ? b.reminders.map(r => r.offsetMinutes) : [],
      _hasPayment: !!p, ...recurrenceToFields(b.recurrence) });
    setFormOpen(true);
  };
  const buildPayment = () => {
    // Only send payment if the user entered a price, chose Free/Paid, or edited an existing payment.
    const hasInput = form.payPrice !== '' || form.payAmount !== '' || form.payStatus === 'free' || form.payStatus === 'paid' || form._hasPayment;
    if (!hasInput) return undefined;
    const toCents = (v) => Math.round((parseFloat(v) || 0) * 100);
    return {
      status: form.payStatus,
      priceCents: toCents(form.payPrice),
      amountPaidCents: toCents(form.payAmount),
      method: form.payMethod === 'not_specified' ? null : form.payMethod,
      note: form.payNote || '',
    };
  };
  const seriesCount = (b, scope) => {
    if (!b?.seriesId || scope === 'this') return 1;
    const same = bookings.filter(x => x.seriesId === b.seriesId);
    if (scope === 'series') return same.length;
    return same.filter(x => x.date >= b.date).length;
  };
  const paymentBadge = (b) => {
    const p = b.payment;
    if (!p) return null;
    const eur = (c) => `€${((c || 0) / 100).toFixed(2)}`;
    if (p.paymentStatus === 'free') return { label: t('bookingCal.free', 'FREE'), cls: 'bg-gray-200 text-gray-700' };
    const price = p.priceCents || 0, paid = p.amountPaidCents || 0, bal = price - paid;
    if (bal <= 0) return { label: `${eur(price)} ${t('bookingCal.paidBadge', 'PAID')}`, cls: 'bg-emerald-100 text-emerald-700' };
    if (paid > 0) return { label: `${eur(paid)} · ${eur(bal)} ${t('bookingCal.dueBadge', 'DUE')}`, cls: 'bg-amber-100 text-amber-800' };
    return { label: `${eur(price)} ${t('bookingCal.unpaidBadge', 'UNPAID')}`, cls: 'bg-red-100 text-red-700' };
  };
  // Map a stored recurrence rule back onto the Repeat form fields (for editing).
  const recurrenceToFields = (r) => {
    if (!r) return { repeat: 'none', customFreq: 'weekly', customInterval: 1, customWeekdays: [],
                     endType: 'count', endCount: 10, endDate: '' };
    let repeat = 'custom', customFreq = r.freq, customInterval = r.interval || 1,
        customWeekdays = Array.isArray(r.weekdays) ? r.weekdays : [];
    const noWk = !customWeekdays.length;
    if (r.freq === 'daily' && customInterval === 1) repeat = 'daily';
    else if (r.freq === 'weekly' && customInterval === 1 && noWk) repeat = 'weekly';
    else if (r.freq === 'weekly' && customInterval === 2 && noWk) repeat = 'biweekly';
    else if (r.freq === 'monthly' && customInterval === 1) repeat = 'monthly';
    return { repeat, customFreq, customInterval, customWeekdays,
             endType: r.endType || 'count', endCount: r.count || 10, endDate: r.endDate || '' };
  };
  // Canonical key so we can tell if the repeat setting actually changed on edit.
  const recKey = (r) => {
    if (!r) return 'none';
    return [r.freq, r.interval || 1, (r.weekdays || []).slice().sort().join('.'),
            r.endType, r.endType === 'date' ? (r.endDate || '') : (r.count || '')].join('|');
  };
  // Plain-English summary of a recurrence rule, e.g. "Repeats every week until 12 August 2027".
  const WD_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const describeRecurrence = (r) => {
    if (!r) return '';
    const n = r.interval || 1;
    let base = '';
    if (r.freq === 'daily') {
      base = n === 1 ? t('bookingCal.recEveryDay', 'every day') : t('bookingCal.recEveryNDays', 'every {n} days').replace('{n}', n);
    } else if (r.freq === 'weekly') {
      base = n === 1 ? t('bookingCal.recEveryWeek', 'every week')
        : n === 2 ? t('bookingCal.recEvery2Weeks', 'every 2 weeks')
        : t('bookingCal.recEveryNWeeks', 'every {n} weeks').replace('{n}', n);
      if (Array.isArray(r.weekdays) && r.weekdays.length) {
        base += ' ' + t('bookingCal.recOn', 'on') + ' ' + r.weekdays.map(d => WD_SHORT[d]).filter(Boolean).join(', ');
      }
    } else if (r.freq === 'monthly') {
      base = n === 1 ? t('bookingCal.recMonthly', 'monthly') : t('bookingCal.recEveryNMonths', 'every {n} months').replace('{n}', n);
    }
    let end = '';
    if (r.endType === 'date' && r.endDate) {
      let d = r.endDate;
      try { d = new Date(r.endDate + 'T00:00:00').toLocaleDateString(i18n.language || undefined, { day: 'numeric', month: 'long', year: 'numeric' }); } catch (e) { /* keep raw */ }
      end = ' ' + t('bookingCal.recUntil', 'until {d}').replace('{d}', d);
    } else if (r.endType === 'count' && r.count) {
      end = ' ' + t('bookingCal.recForN', 'for {n} occurrences').replace('{n}', r.count);
    }
    return `${t('bookingCal.repeatsPrefix', 'Repeats')} ${base}${end}`.replace(/\s+/g, ' ').trim();
  };
  const buildRecurrence = () => {
    if (form.repeat === 'none') return null;
    const presets = {
      daily: { freq: 'daily', interval: 1 },
      weekly: { freq: 'weekly', interval: 1 },
      biweekly: { freq: 'weekly', interval: 2 },
      monthly: { freq: 'monthly', interval: 1 },
    };
    let r;
    if (form.repeat === 'custom') {
      r = { freq: form.customFreq, interval: Math.max(1, parseInt(form.customInterval, 10) || 1) };
      if (form.customFreq === 'weekly' && form.customWeekdays.length) r.weekdays = form.customWeekdays;
    } else {
      r = { ...presets[form.repeat] };
    }
    r.endType = form.endType;
    if (form.endType === 'date') r.endDate = form.endDate;
    else r.count = Math.max(1, parseInt(form.endCount, 10) || 1);
    return r;
  };
  const saveForm = async () => {
    if (!form.clientName.trim()) { toast.error(t('bookingCal.clientRequired', 'Client name is required')); return; }
    if (!form.date || !form.startTime || !form.endTime) {
      toast.error(t('bookingCal.dateTimeRequired', 'Please select a date, start time and end time.')); return;
    }
    if (form.endTime <= form.startTime) {
      toast.error(t('bookingCal.endAfterStart', 'End time must be later than start time.')); return;
    }
    if ((form.notes || '').length > NOTES_MAX) {
      toast.error(t('bookingCal.notesTooLong', 'Notes are too long. Maximum {n} characters.').replace('{n}', NOTES_MAX.toLocaleString())); return;
    }
    if (form.repeat !== 'none' && form.endType === 'date' && !form.endDate) {
      toast.error(t('bookingCal.endDateRequired', 'Please choose an end date for the repeat')); return;
    }
    const payment = buildPayment();
    if (payment && payment.status !== 'free' && payment.amountPaidCents > payment.priceCents) {
      toast.error(t('bookingCal.paidExceeds', 'Amount paid cannot exceed the price')); return;
    }
    // Editing a booking that belongs to a duplicate group (>1) → ask whether
    // to change just this one or the whole group, before saving.
    if (editing && editing.dupGroupId && dupGroupBookings(editing).length > 1) {
      setDupEditOpen(true); return;
    }
    await performSave(editing ? editScope : null);
  };

  const performSave = async (scopeOverride) => {
    const payment = buildPayment();
    setSaving(true);
    try {
      if (editing) {
        const scope = scopeOverride || editScope;
        const body = {
          clientName: form.clientName, service: form.service, phone: form.phone,
          email: form.email, notes: form.notes, status: form.status, categoryId: form.categoryId || '',
          assigneeUserId: form.assigneeUserId || '',
          reminderOffsets: form.reminderOffsets || [],
        };
        if (payment) body.payment = payment;
        // Only a whole-group edit propagates the clock time (each booking keeps
        // its own date). "This booking only" keeps the existing details-only edit.
        if (scope === 'dupgroup') { body.startTime = form.startTime; body.endTime = form.endTime; }
        await axiosInstance.patch(`/calendar/calendars/${calendar.id}/bookings/${editing.id}?scope=${scope}`, body);
        // Date / start / end edit for THIS booking — reuse the EXISTING reschedule
        // endpoint (updates the same booking + recomputes reminder fire times).
        // Runs only when a value actually changed; the whole-group clock-time
        // change is already handled above so we skip it here.
        if (scope !== 'dupgroup' &&
            (form.date !== editing.date || form.startTime !== editing.startTime || form.endTime !== editing.endTime)) {
          await axiosInstance.post(`/calendar/calendars/${calendar.id}/bookings/${editing.id}/reschedule`,
            { date: form.date, startTime: form.startTime, endTime: form.endTime });
        }
        // Add / change / remove the REPEAT pattern — only when it actually changed.
        // Runs after the detail + time edits so regenerated occurrences inherit
        // the latest details. Backend guarantees no duplicate bookings.
        const newRec = buildRecurrence();
        const oldRec = editing.recurrence || null;
        let recMsg = null;
        if (recKey(newRec) !== recKey(oldRec)) {
          const recScope = editing.seriesId ? scope : 'series';
          const rr = await axiosInstance.post(
            `/calendar/calendars/${calendar.id}/bookings/${editing.id}/set-recurrence?scope=${recScope}`,
            { recurrence: newRec, scope: recScope });
          recMsg = newRec
            ? t('bookingCal.repeatUpdated', 'Repeat updated — {n} bookings').replace('{n}', rr.data.occurrences || '')
            : t('bookingCal.repeatRemoved', 'This booking no longer repeats');
        }
        toast.success(recMsg || (scope === 'dupgroup'
          ? t('bookingCal.savedDupGroup', 'All duplicated bookings updated')
          : t('bookingCal.savedEdit', 'Booking updated')));
      } else {
        const rec = buildRecurrence();
        const payload = { ...form };
        ['repeat', 'customFreq', 'customInterval', 'customWeekdays', 'endType', 'endCount', 'endDate',
         'payStatus', 'payPrice', 'payAmount', 'payMethod', 'payNote', '_hasPayment'].forEach(k => delete payload[k]);
        if (rec) payload.recurrence = rec;
        if (payment) payload.payment = payment;
        const r = await axiosInstance.post(`/calendar/calendars/${calendar.id}/bookings`, payload);
        toast.success(rec ? t('bookingCal.savedSeries', 'Added {n} bookings').replace('{n}', r.data.occurrences)
          : t('bookingCal.savedNew', 'Booking added'));
      }
      setDupEditOpen(false);
      setFormOpen(false);
      await loadBookings(calendar.id);
    } catch (e) {
      toast.error(errMsg(e, t('bookingCal.saveFailed', 'Could not save booking')));
    } finally { setSaving(false); }
  };
  const openDuplicate = (b) => {
    setDupSource(b);
    setDupDates([]);
    setDupMode('pick');
    setDupRangeFrom(b.date || '');
    setDupRangeTo(b.date || '');
    setDupStart(b.startTime || '09:00');
    setDupEnd(b.endTime || '10:00');
    setDupCopyReminders(false);
    // Stable group id per duplication session → safe to retry after a failure
    // (backend skips dates already created for this group; no duplicates).
    setDupGroupId(window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setDupOpen(true);
  };
  const DUP_MAX_DATES = 366;
  // Effective list of dates to duplicate to, based on the chosen mode.
  const getDupDates = () => {
    if (dupMode === 'range') {
      if (!dupRangeFrom || !dupRangeTo) return [];
      const start = parseISO(dupRangeFrom), end = parseISO(dupRangeTo);
      if (isNaN(start) || isNaN(end) || end < start) return [];
      const out = []; const d = new Date(start);
      while (d <= end && out.length <= DUP_MAX_DATES + 1) { out.push(new Date(d)); d.setDate(d.getDate() + 1); }
      return out;
    }
    return dupDates;
  };
  const doDuplicate = async () => {
    if (dupBusy) return;                 // guard against double submission
    const b = dupSource;
    if (!b) return;
    const targetDates = getDupDates();
    if (!targetDates.length) {
      toast.error(dupMode === 'range'
        ? t('bookingCal.dupPickRange', 'Please choose a valid From and To date.')
        : t('bookingCal.dupPickDate', 'Please select at least one date.'));
      return;
    }
    if (targetDates.length > DUP_MAX_DATES) {
      toast.error(t('bookingCal.dupTooMany', 'That range is too large (max {n} days). Please choose a shorter range.').replace('{n}', DUP_MAX_DATES));
      return;
    }
    if (!dupStart || !dupEnd) { toast.error(t('bookingCal.dupNeedTimes', 'Please choose a start and end time.')); return; }
    if (dupEnd <= dupStart) { toast.error(t('bookingCal.dupEndAfterStart', 'End time must be after the start time.')); return; }
    setDupBusy(true);
    const p = b.payment;
    const payment = p ? {
      status: p.paymentStatus || 'unpaid',
      priceCents: p.priceCents || 0,
      amountPaidCents: p.amountPaidCents || 0,
      method: (p.paymentMethod && p.paymentMethod !== 'not_specified') ? p.paymentMethod : null,
      note: p.paymentNote || '',
    } : undefined;
    const reminderOffsets = (dupCopyReminders && Array.isArray(b.reminders)) ? b.reminders.map(r => r.offsetMinutes) : [];
    const dateStrs = [...targetDates].sort((a, z) => a - z).map(d => format(d, 'yyyy-MM-dd'));
    // ONE bulk request for all dates — avoids per-date rapid calls that tripped
    // the API rate limit. Idempotent on dupGroupId so a retry won't duplicate.
    const body = {
      dates: dateStrs, startTime: dupStart, endTime: dupEnd,
      clientName: b.clientName, service: b.service || '', phone: b.phone || '',
      email: b.email || '', notes: b.notes || '', status: b.status || 'confirmed',
      categoryId: b.categoryId || '', reminderOffsets, dupGroupId,
    };
    if (payment) body.payment = payment;
    try {
      const r = await axiosInstance.post(`/calendar/calendars/${calendar.id}/bookings/bulk`, body);
      const created = r.data?.created ?? dateStrs.length;
      await loadBookings(calendar.id);
      setDupBusy(false);
      setDupOpen(false);
      toast.success(t('bookingCal.dupCreated', 'Created {n} independent booking(s)').replace('{n}', created));
    } catch (e) {
      await loadBookings(calendar.id);
      setDupBusy(false);
      toast.error(t('bookingCal.dupFailedRetry', 'Could not create the bookings. Please try again in a moment.'));
    }
  };
  const openReschedule = (b) => { setRescheduleFor(b); setReForm({ date: b.date, startTime: b.startTime, endTime: b.endTime }); };
  const doReschedule = async () => {
    try {
      await axiosInstance.post(`/calendar/calendars/${calendar.id}/bookings/${rescheduleFor.id}/reschedule`, reForm);
      toast.success(t('bookingCal.rescheduled', 'Booking rescheduled'));
      setRescheduleFor(null); await loadBookings(calendar.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const doCancel = async () => {
    try {
      await axiosInstance.post(`/calendar/calendars/${calendar.id}/bookings/${confirmCancel.id}/cancel?scope=${cancelScope}`);
      toast.success(t('bookingCal.cancelled', 'Booking cancelled'));
      setConfirmCancel(null); await loadBookings(calendar.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const doDelete = async () => {
    try {
      await axiosInstance.delete(`/calendar/calendars/${calendar.id}/bookings/${confirmDelete.id}?scope=${deleteScope}`);
      toast.success(t('bookingCal.deleted', 'Booking moved to Recycle Bin'));
      setConfirmDelete(null); await loadBookings(calendar.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  // Permanent (hard) delete — no Recycle Bin, cannot be undone. History/payment
  // records are preserved server-side.
  const openPurge = (b) => { setPurgeScope('this'); setConfirmPurge(b); };
  const doPurge = async () => {
    const target = confirmPurge, scope = purgeScope;
    try {
      await axiosInstance.post(`/calendar/calendars/${calendar.id}/bookings/${target.id}/purge?scope=${scope}`);
      toast.success(t('bookingCal.purged', 'Appointment permanently deleted'));
      setConfirmPurge(null);
      // Optimistically drop the purged booking(s) so cards disappear at once
      // (prevents a confusing 2-3s lingering card + accidental double-click).
      setBookings(prev => prev.filter(x => {
        if (x.id === target.id) return false;
        if (target.seriesId && x.seriesId === target.seriesId) {
          if (scope === 'series') return false;
          if (scope === 'future' && x.date >= target.date) return false;
        }
        return true;
      }));
      loadBookings(calendar.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  // Skip ONE occurrence of a recurring series (soft-delete just this date -> Recycle Bin).
  // The rest of the series is untouched; it can be un-skipped by restoring from the bin.
  const doSkip = async () => {
    try {
      await axiosInstance.delete(`/calendar/calendars/${calendar.id}/bookings/${confirmSkip.id}?scope=this`);
      toast.success(t('bookingCal.dateSkipped', 'This date was skipped — the rest of the repeat is unchanged'));
      setConfirmSkip(null); await loadBookings(calendar.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  // --- Duplicate-group delete: one / selected / all ---
  const dupGroupBookings = (b) => (b?.dupGroupId
    ? bookings.filter(x => x.dupGroupId && x.dupGroupId === b.dupGroupId && !x.deletedAt)
        .sort((a, z) => (a.date + a.startTime).localeCompare(z.date + z.startTime))
    : []);
  const openDelete = (b) => {
    // Bookings created together via Duplicate (dupGroupId) get the group menu;
    // everything else (normal + legacy duplicates without a group) uses the
    // standard single-delete confirmation unchanged.
    if (b.dupGroupId && dupGroupBookings(b).length > 1) {
      setDupDelFor(b); setDupDelMode('menu'); setDupDelSel(new Set());
    } else {
      setDeleteScope('this'); setConfirmDelete(b);
    }
  };
  const _deleteIds = async (ids) => {
    let ok = 0; const failed = [];
    for (const id of ids) {
      try { await axiosInstance.delete(`/calendar/calendars/${calendar.id}/bookings/${id}?scope=this`); ok++; }
      catch (e) { failed.push(errMsg(e)); }
    }
    await loadBookings(calendar.id);
    return { ok, failed };
  };
  const dupDeleteThisOnly = async () => {
    if (dupDelBusy) return; setDupDelBusy(true);
    const { ok } = await _deleteIds([dupDelFor.id]);
    setDupDelBusy(false); setDupDelFor(null);
    if (ok) toast.success(t('bookingCal.deleted', 'Booking moved to Recycle Bin'));
  };
  const dupDeleteSelected = async () => {
    if (dupDelBusy) return;
    const ids = [...dupDelSel];
    if (!ids.length) { toast.error(t('bookingCal.dupDelPickOne', 'Please select at least one booking.')); return; }
    setDupDelBusy(true);
    const { ok, failed } = await _deleteIds(ids);
    setDupDelBusy(false); setDupDelFor(null);
    if (ok && !failed.length) toast.success(t('bookingCal.dupDelDone', 'Deleted {n} booking(s)').replace('{n}', ok));
    else toast.error(t('bookingCal.dupDelPartial', 'Deleted {ok}; {fail} failed').replace('{ok}', ok).replace('{fail}', failed.length));
  };
  const dupDeleteAll = async () => {
    if (dupDelBusy) return; setDupDelBusy(true);
    // ONE server-side request: the backend resolves the whole dupGroup and soft-
    // deletes every matching booking (Recycle Bin) in a single call. Avoids the
    // old per-booking DELETE loop that tripped the IP rate limiter on big groups.
    try {
      const r = await axiosInstance.delete(`/calendar/calendars/${calendar.id}/bookings/${dupDelFor.id}?scope=dupgroup`);
      const n = r.data?.affected ?? 0;
      toast.success(t('bookingCal.dupDelDone', 'Deleted {n} booking(s)').replace('{n}', n));
    } catch (e) {
      toast.error(errMsg(e, t('bookingCal.dupDelFailed', 'Could not delete the duplicated bookings')));
    } finally {
      setDupDelBusy(false); setDupDelFor(null);
      await loadBookings(calendar.id);
    }
  };
  const doRestore = async (b) => {
    try {
      await axiosInstance.post(`/calendar/calendars/${calendar.id}/bookings/${b.id}/restore`);
      toast.success(t('bookingCal.restored', 'Booking restored'));
      await loadRecycle(calendar.id); await loadBookings(calendar.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const openAudit = async (b) => {
    setAuditFor(b); setAuditRows([]);
    try {
      const r = await axiosInstance.get(`/calendar/calendars/${calendar.id}/audit?bookingId=${b.id}`);
      setAuditRows(r.data.audit || []);
    } catch { /* noop */ }
  };
  const backupNow = () => {
    setExportFrom('');
    setExportTo('');
    setExportOpen(true);
  };
  const runRangeExport = async () => {
    if (!exportFrom || !exportTo) { toast.error(t('bookingCal.exportPickBoth', 'Please pick both a From and To date')); return; }
    if (exportFrom > exportTo) { toast.error(t('bookingCal.exportRangeInvalid', 'From date must be on or before To date')); return; }
    setExporting(true);
    try {
      const res = await axiosInstance.get(
        `/calendar/calendars/${calendar.id}/backup.csv?from_date=${exportFrom}&to_date=${exportTo}`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(calendar.name || 'calendar').replace(/\s+/g, '_')}_bookings_${exportFrom}_to_${exportTo}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      setExportOpen(false);
      toast.success(t('bookingCal.exportDone', 'Excel (CSV) downloaded'));
    } catch { toast.error(t('bookingCal.backupFailed', 'Backup failed')); }
    finally { setExporting(false); }
  };
  const goToday = () => { setMode('calendar'); setMonth(new Date()); setSelectedDate(new Date()); };

  const goToDate = (d) => { setSelectedDate(d); setMonth(d); try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { window.scrollTo(0, 0); } };
  const goPrevDay = () => goToDate(subDays(selectedDate, 1));
  const goNextDay = () => goToDate(addDays(selectedDate, 1));
  const goTodayDayNav = () => goToDate(new Date());

  // ---- self-service calendars ----
  const owned = useMemo(() => allCalendars.filter(c => c.myRole === 'owner'), [allCalendars]);
  const sharedWithMe = useMemo(() => allCalendars.filter(c => c.myRole !== 'owner'), [allCalendars]);
  const isOwner = role === 'owner';
  const canManageMembers = role === 'owner' || role === 'admin';

  const createCalendar = async () => {
    if (!newCalName.trim()) { toast.error(t('bookingCal.calNameRequired', 'Calendar name is required')); return; }
    try {
      const body = { name: newCalName.trim() };
      if (newCalMunicipality) body.municipality = newCalMunicipality;
      const r = await axiosInstance.post('/calendar/calendars', body);
      setNewCalName(''); setNewCalMunicipality(''); setCreateOpen(false);
      await loadCalendar();
      if (r.data?.calendar) selectCalendar(r.data.calendar);
      toast.success(t('bookingCal.calCreated', 'Calendar created'));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const renameCalendar = async () => {
    if (!renameVal.trim()) return;
    try {
      // Always send municipality so the owner can also CLEAR it ("" = clear).
      await axiosInstance.patch(`/calendar/calendars/${calendar.id}`, {
        name: renameVal.trim(),
        municipality: renameMunicipality || '',
      });
      setRenameOpen(false);
      await loadCalendar();
      toast.success(t('bookingCal.calSaved', 'Calendar saved'));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteCalendarFn = async () => {
    try {
      await axiosInstance.delete(`/calendar/calendars/${calendar.id}`);
      setConfirmDeleteCal(false);
      setSelectedCalId(null);
      await loadCalendar();
      toast.success(t('bookingCal.calDeleted', 'Calendar deleted — recoverable for 30 days'));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const restoreCalendar = async (cal) => {
    try {
      await axiosInstance.post(`/calendar/calendars/${cal.id}/restore`);
      await loadCalendar();
      toast.success(t('bookingCal.calRestored', 'Calendar restored'));
    } catch (e) { toast.error(errMsg(e)); }
  };

  // ---- members ----
  const loadMembers = useCallback(async (calId) => {
    if (!calId) return;
    try {
      const r = await axiosInstance.get(`/calendar/calendars/${calId}/members`);
      setMembers(r.data.members || []);
      setPendingInvites(r.data.pendingInvites || []);
    } catch { /* noop */ }
  }, []);
  const openMembers = async () => { setMembersOpen(true); await loadMembers(calendar.id); };
  // Keep the member list ready so the "Assigned to" dropdown (single + multi-
  // date forms) is populated without opening the Members dialog first.
  useEffect(() => { if (calendar?.id) loadMembers(calendar.id); }, [calendar?.id, loadMembers]);
  const inviteMember = async () => {
    if (!inviteEmail.trim()) { toast.error(t('bookingCal.emailRequired', 'Email is required')); return; }
    setBusyMembers(true);
    try {
      const r = await axiosInstance.post(`/calendar/calendars/${calendar.id}/members`, { email: inviteEmail.trim(), role: inviteRole });
      setInviteEmail('');
      await loadMembers(calendar.id);
      const pending = r.data?.status === 'pending';
      const emailSent = r.data?.emailSent;
      if (emailSent === false) {
        toast.warning(t('bookingCal.inviteNoEmail', 'Calendar access was created, but the invitation email could not be sent.'));
      } else if (pending) {
        toast.success(t('bookingCal.invitePendingSent', 'Invitation saved and invitation email sent. Their access will activate when they join Find-2-Day.'));
      } else {
        toast.success(t('bookingCal.inviteAddedSent', 'Calendar access granted and invitation email sent.'));
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusyMembers(false); }
  };
  const changeMemberRole = async (m, newRole) => {
    try {
      await axiosInstance.patch(`/calendar/calendars/${calendar.id}/members/${m.userId}`, { role: newRole });
      await loadMembers(calendar.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const removeMember = async (m) => {
    try {
      await axiosInstance.delete(`/calendar/calendars/${calendar.id}/members/${m.userId}`);
      await loadMembers(calendar.id);
      toast.success(t('bookingCal.memberRemoved', 'Member removed'));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setConfirmRemoveMember(null); }
  };
  const transferOwnership = async (m) => {
    try {
      await axiosInstance.post(`/calendar/calendars/${calendar.id}/transfer-ownership`, { userId: m.userId });
      setMembersOpen(false);
      await loadCalendar();
      toast.success(t('bookingCal.ownershipTransferred', 'Ownership transferred'));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const ROLE_LABELS = {
    owner: t('bookingCal.roleOwner', 'Owner'), admin: t('bookingCal.roleAdmin', 'Admin'),
    member: t('bookingCal.roleMember', 'Member'), view_only: t('bookingCal.roleView', 'View only'),
  };

  // ---- category management ----
  const addCategory = async () => {
    if (!newCatName.trim()) { toast.error(t('bookingCal.catNameRequired', 'Category name is required')); return; }
    try {
      await axiosInstance.post(`/calendar/calendars/${calendar.id}/categories`, { name: newCatName.trim(), color: newCatColor });
      setNewCatName(''); setNewCatColor(PALETTE[4]);
      await loadCategories(calendar.id);
      toast.success(t('bookingCal.catAdded', 'Category added'));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const renameCategory = async (cat, name) => {
    if (!name.trim() || name === cat.name) return;
    try {
      await axiosInstance.patch(`/calendar/calendars/${calendar.id}/categories/${cat.id}`, { name: name.trim() });
      await loadCategories(calendar.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const recolorCategory = async (cat, color) => {
    try {
      await axiosInstance.patch(`/calendar/calendars/${calendar.id}/categories/${cat.id}`, { color });
      await loadCategories(calendar.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteCategory = async () => {
    try {
      await axiosInstance.delete(`/calendar/calendars/${calendar.id}/categories/${catToDelete.id}`);
      setCatToDelete(null);
      await loadCategories(calendar.id);
      await loadBookings(calendar.id);
      toast.success(t('bookingCal.catDeleted', 'Category deleted — bookings kept'));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const holidayName = (h) => t(h.nameKey, h.name || h.nameKey);

  // ---- birthdays (separate lightweight yearly type; never a booking) ----
  const BDAY_REMINDER_OPTS = ['none', 'on_day', '1_day', '1_week'];
  const isLeapYear = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  // Does this birthday fall on 'YYYY-MM-DD'? 29 Feb → 28 Feb in non-leap years.
  const birthdayMatchesDate = (bd, ds) => {
    const [y, m, d] = ds.split('-').map(Number);
    let day = bd.day, month = bd.month;
    if (month === 2 && day === 29 && !isLeapYear(y)) day = 28;
    return month === m && day === d;
  };
  const birthdaysForDate = (ds) => birthdays.filter(bd => birthdayMatchesDate(bd, ds));
  const monthName = (m) => format(new Date(2000, m - 1, 1), 'MMMM');
  const bdayReminderLabel = (r) => ({
    none: t('bookingCal.remindNone', 'No reminder'),
    on_day: t('bookingCal.bdayOnDay', 'On the day'),
    '1_day': t('bookingCal.bday1Day', '1 day before'),
    '1_week': t('bookingCal.bday1Week', '1 week before'),
  }[r] || r);
  const openAddBirthday = () => {
    setBdayEditing(null);
    setBdayForm({ personName: '', day: String(selectedDate.getDate()), month: String(selectedDate.getMonth() + 1), reminder: 'none' });
    setBdayOpen(true);
  };
  const openEditBirthday = (bd) => {
    setBdayEditing(bd);
    setBdayForm({ personName: bd.personName, day: String(bd.day), month: String(bd.month), reminder: bd.reminder || 'none' });
    setBdayOpen(true);
  };
  const saveBirthday = async () => {
    if (!bdayForm.personName.trim()) { toast.error(t('bookingCal.bdayNameRequired', 'Please enter a name')); return; }
    const body = { personName: bdayForm.personName.trim(), day: parseInt(bdayForm.day, 10), month: parseInt(bdayForm.month, 10), reminder: bdayForm.reminder };
    try {
      if (bdayEditing) await axiosInstance.patch(`/calendar/calendars/${calendar.id}/birthdays/${bdayEditing.id}`, body);
      else await axiosInstance.post(`/calendar/calendars/${calendar.id}/birthdays`, body);
      setBdayOpen(false);
      await loadBirthdays(calendar.id);
      toast.success(bdayEditing ? t('bookingCal.bdaySaved', 'Birthday updated') : t('bookingCal.bdayAdded', 'Birthday added'));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteBirthday = async () => {
    try {
      await axiosInstance.delete(`/calendar/calendars/${calendar.id}/birthdays/${bdayToDelete.id}`);
      setBdayToDelete(null);
      await loadBirthdays(calendar.id);
      toast.success(t('bookingCal.bdayDeleted', 'Birthday deleted'));
    } catch (e) { toast.error(errMsg(e)); }
  };
  // ONLY when it matches THIS calendar's municipality (never another town's).
  const calMunicipality = calendar?.municipality || null;
  const holidaysForDate = (ds) => {
    const universal = UNIVERSAL_HOLIDAYS_BY_DATE[ds] || [];
    const local = calMunicipality
      ? (LOCAL_HOLIDAYS_BY_DATE[ds] || []).filter(h => h.town === calMunicipality)
      : [];
    return [...universal, ...local];
  };
  // Read-only holiday chips (National/Andalucía/Local) render via the hoisted
  // <HolidayChips> component using holidaysForDate/holidayName passed in ctx.

  const DayContent = (dp) => {
    const ds = format(dp.date, 'yyyy-MM-dd');
    const colors = dotsByDate[ds] || [];
    const shown = colors.slice(0, 3);
    const hols = holidaysForDate(ds);
    const hasBday = birthdaysForDate(ds).length > 0;
    return (
      <div className="flex flex-col items-center justify-center leading-none relative">
        {(hols.length > 0 || hasBday) && (
          <span className="absolute -top-0.5 right-0 flex gap-[1px] items-center" aria-hidden>
            {hasBday && <span className="text-[8px] leading-none" data-testid={`calendar-bday-dot-${ds}`}>🎂</span>}
            {hols.slice(0, 2).map((h, i) => (
              <span key={i} style={{ backgroundColor: (HOLIDAY_SCOPE_META[h.scope] || HOLIDAY_SCOPE_META.national).dot, width: 5, height: 5 }} className="rounded-full inline-block ring-1 ring-white" />
            ))}
          </span>
        )}
        <span>{dp.date.getDate()}</span>
        <span className="flex gap-[2px] mt-[2px] items-center" style={{ height: 6 }}>
          {shown.map((d, i) => <span key={i} style={{ backgroundColor: d.color, width: 6, height: 6 }} className="rounded-full inline-block" />)}
          {colors.length > 3 && <span className="text-[9px] font-bold text-gray-600 leading-none">+</span>}
        </span>
      </div>
    );
  };

  // Everything the hoisted <BookingCard>/<DateGroup>/<HolidayChips>/<BirthdayChips>
  // need. Rebuilt each render (cheap) — the components' identity stays stable so
  // the list reconciles in place instead of remounting (preserves scroll).
  const cardCtx = {
    catMap, canWrite, canDelete, canDeleteBooking, t, paymentBadge, describeRecurrence,
    holidaysForDate, birthdaysForDate, holidayName,
    openEdit, openReschedule, openDuplicate, openDelete, openPurge, openAudit,
    setCancelScope, setConfirmCancel, setConfirmSkip,
  };

  // ---- render ----
  if (!user) {
    return <div className="p-8 text-center" data-testid="calendar-signin-prompt">
      <p className="mb-4">{t('bookingCal.signInNeeded', 'Please sign in to view your calendar.')}</p>
      <Button onClick={() => navigate('/signin')} data-testid="calendar-signin-btn">{t('signIn', 'Sign In')}</Button>
    </div>;
  }
  if (loading) return <div className="p-8 text-center text-gray-500" data-testid="calendar-loading">{t('loading', 'Loading...')}</div>;
  if (!calendar) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center" data-testid="calendar-none">
        <CalendarDays className="h-10 w-10 mx-auto text-gray-300 mb-3" />
        <h1 className="text-lg font-semibold text-gray-800">{t('bookingCal.noAccessTitle', 'No calendars yet')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('bookingCal.createFirstBody', 'Create your own private calendar, or wait to be invited to one.')}</p>
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button onClick={() => { setNewCalName(''); setCreateOpen(true); }} data-testid="calendar-create-first-btn">
            <Plus className="h-4 w-4 mr-1" />{t('bookingCal.createCalendar', 'Create calendar')}
          </Button>
          <Button variant="outline" onClick={() => navigate('/profile')} data-testid="calendar-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />{t('back', 'Back')}
          </Button>
        </div>
        {deletedCalendars.length > 0 && (
          <div className="mt-6 text-left max-w-sm mx-auto" data-testid="calendar-deleted-list-empty">
            <p className="text-xs font-semibold text-gray-500 mb-1">{t('bookingCal.recentlyDeleted', 'Recently deleted')}</p>
            {deletedCalendars.map(c => (
              <div key={c.id} className="flex items-center justify-between border rounded-lg px-3 py-2 mb-1">
                <span className="text-sm truncate">{c.name}</span>
                <Button size="sm" variant="ghost" onClick={() => restoreCalendar(c)} data-testid={`calendar-restore-${c.id}`}>
                  <RotateCw className="h-4 w-4 mr-1" />{t('bookingCal.restore', 'Restore')}
                </Button>
              </div>
            ))}
          </div>
        )}
        {/* Create dialog (available even with no calendar) */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-sm" data-testid="create-calendar-dialog">
            <DialogHeader>
              <DialogTitle>{t('bookingCal.createCalendar', 'Create calendar')}</DialogTitle>
              <DialogDescription>{t('bookingCal.createCalHint', 'Your new calendar is private. You become its owner.')}</DialogDescription>
            </DialogHeader>
            <Input autoFocus placeholder={t('bookingCal.calNamePlaceholder', 'e.g. Personal, Work, Family')} value={newCalName} onChange={e => setNewCalName(e.target.value)} data-testid="create-calendar-name" />
            <div className="mt-2">
              <Label className="text-[11px]">{t('bookingCal.municipality', 'Municipality (optional)')}</Label>
              <select value={newCalMunicipality} onChange={e => setNewCalMunicipality(e.target.value)} className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white mt-1" data-testid="create-calendar-municipality">
                <option value="">{t('bookingCal.municipalityNone', 'None — national & Andalucía holidays only')}</option>
                {townOptions.map(tn => <option key={tn} value={tn}>{tn}</option>)}
              </select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="create-calendar-cancel">{t('cancel', 'Cancel')}</Button>
              <Button onClick={createCalendar} data-testid="create-calendar-save">{t('bookingCal.createCalendar', 'Create calendar')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  const NavBtn = ({ id, icon: Icon, label, onClick, testId }) => {
    const active = mode === id;
    return (
      <button
        onClick={onClick}
        data-testid={testId}
        className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg text-[11px] font-medium transition-colors ${active ? 'bg-white text-purple-700 shadow-sm' : 'text-white/80 hover:text-white'}`}
      >
        <Icon className="h-4 w-4" />{label}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="calendar-page">
      {/* Dedicated Calendar header band */}
      <div className="bg-gradient-to-br from-purple-700 to-purple-600 text-white">
        <div className="max-w-3xl mx-auto px-4 pt-4 pb-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <button onClick={() => navigate('/profile')} className="p-1 text-white/80 hover:text-white" data-testid="calendar-header-back">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-1.5 min-w-0 max-w-full" data-testid="calendar-switcher-trigger">
                      <h1 className="text-base font-bold truncate flex items-center gap-2" data-testid="calendar-title">
                        <CalendarDays className="h-5 w-5 shrink-0" /><span className="truncate">{calendar.name}</span>
                      </h1>
                      <ChevronDown className="h-4 w-4 shrink-0 text-white/70" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-64" data-testid="calendar-switcher-menu">
                    <DropdownMenuLabel>{t('bookingCal.myCalendars', 'My calendars')}</DropdownMenuLabel>
                    {owned.map(c => (
                      <DropdownMenuItem key={c.id} onClick={() => selectCalendar(c)} data-testid={`calendar-switch-${c.id}`}>
                        <CalendarDays className="h-4 w-4 mr-2 opacity-60" /><span className="flex-1 truncate">{c.name}</span>
                        <Badge variant="secondary" className="ml-2 text-[10px]">{ROLE_LABELS[c.myRole]}</Badge>
                      </DropdownMenuItem>
                    ))}
                    {sharedWithMe.length > 0 && <DropdownMenuLabel className="text-[11px] text-gray-400">{t('bookingCal.sharedWithMe', 'Shared with me')}</DropdownMenuLabel>}
                    {sharedWithMe.map(c => (
                      <DropdownMenuItem key={c.id} onClick={() => selectCalendar(c)} data-testid={`calendar-switch-${c.id}`}>
                        <Users className="h-4 w-4 mr-2 opacity-60" /><span className="flex-1 truncate">{c.name}</span>
                        <Badge variant="secondary" className="ml-2 text-[10px]">{ROLE_LABELS[c.myRole]}</Badge>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { setNewCalName(''); setCreateOpen(true); }} data-testid="calendar-create-open">
                      <Plus className="h-4 w-4 mr-2" />{t('bookingCal.createCalendar', 'Create calendar')}
                    </DropdownMenuItem>
                    {isOwner && (
                      <DropdownMenuItem onClick={() => { setRenameVal(calendar.name); setRenameMunicipality(calendar.municipality || ''); setRenameOpen(true); }} data-testid="calendar-rename-open">
                        <Pencil className="h-4 w-4 mr-2" />{t('bookingCal.calendarSettings', 'Calendar settings')}
                      </DropdownMenuItem>
                    )}
                    {isOwner && (
                      <DropdownMenuItem onClick={() => setConfirmDeleteCal(true)} className="text-red-600" data-testid="calendar-delete-open">
                        <Trash2 className="h-4 w-4 mr-2" />{t('bookingCal.deleteCalendar', 'Delete calendar')}
                      </DropdownMenuItem>
                    )}
                    {deletedCalendars.length > 0 && <DropdownMenuSeparator />}
                    {deletedCalendars.map(c => (
                      <DropdownMenuItem key={c.id} onClick={() => restoreCalendar(c)} data-testid={`calendar-restore-${c.id}`}>
                        <RotateCw className="h-4 w-4 mr-2 opacity-60" />{t('bookingCal.restore', 'Restore')}: <span className="truncate ml-1">{c.name}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <p className="text-[11px] text-white/70" data-testid="calendar-role">{t('bookingCal.yourRole', 'Your role')}: <span className="font-semibold uppercase">{ROLE_LABELS[role] || role}</span></p>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-wrap justify-end">
              <label
                className="flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-white/15 hover:bg-white/25 text-white text-[12px] font-medium cursor-pointer select-none"
                data-testid="calendar-notify-pref-label"
                title={t('bookingCal.notifyOnChangeFull', 'Notify me when shared calendar bookings change')}
              >
                <input
                  type="checkbox"
                  checked={notifyOnChange}
                  onChange={toggleNotify}
                  disabled={savingNotify}
                  data-testid="calendar-notify-pref-checkbox"
                  className="h-4 w-4 accent-purple-600 cursor-pointer disabled:opacity-60"
                />
                <Bell className="h-4 w-4" />
                <span className="hidden sm:inline">{t('bookingCal.notifyMe', 'Notify me')}</span>
              </label>
              {canManageMembers && (
                <Button size="sm" variant="secondary" className="h-8 bg-white/15 hover:bg-white/25 text-white border-0" onClick={openMembers} data-testid="calendar-members-open">
                  <Users className="h-4 w-4" />
                </Button>
              )}
              <Button size="sm" variant="secondary" className="h-8 bg-white/15 hover:bg-white/25 text-white border-0" onClick={() => setMode('recycle')} data-testid="calendar-recycle-open">
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="secondary" className="h-8 bg-white/15 hover:bg-white/25 text-white border-0" onClick={backupNow} data-testid="calendar-backup-now">
                <Download className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">{t('bookingCal.backupNow', 'Backup Now')}</span>
              </Button>
            </div>
          </div>
          {/* In-area navigation */}
          <div className="flex gap-1 mt-3 bg-white/10 rounded-xl p-1" data-testid="calendar-nav">
            <NavBtn id="today" icon={CalendarCheck} label={t('bookingCal.today', 'Today')} onClick={goToday} testId="calendar-nav-today" />
            <NavBtn id="calendar" icon={CalendarDays} label={t('bookingCal.calendarNav', 'Calendar')} onClick={() => setMode('calendar')} testId="calendar-nav-calendar" />
            <NavBtn id="agenda" icon={CalendarRange} label={t('bookingCal.agendaNav', 'Agenda')} onClick={() => setMode('agenda')} testId="calendar-nav-agenda" />
            <NavBtn id="search" icon={SearchIcon} label={t('bookingCal.searchNav', 'Search')} onClick={() => setMode('search')} testId="calendar-nav-search" />
            <NavBtn id="lists" icon={ListChecks} label={t('bookingCal.listsNav', 'Lists')} onClick={() => setMode('lists')} testId="calendar-nav-lists" />
            <NavBtn id="notes" icon={StickyNote} label={t('bookingCal.notesNav', 'Notes')} onClick={() => setMode('notes')} testId="calendar-nav-notes" />
            <button
              onClick={() => navigate('/holidays')}
              data-testid="calendar-nav-holidays"
              className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg text-[11px] font-medium transition-colors text-white/80 hover:text-white"
            >
              <PartyPopper className="h-4 w-4" />{t('bookingCal.holidaysNav', 'Holidays')}
            </button>
          </div>
          {/* Phase 1B — automatic backup status (OWNER/ADMIN only) */}
          {canDelete && backupStatus && (
            <div className="mt-2" data-testid="calendar-backup-status">
              {backupStatus.hasBackup ? (
                backupStatus.status === 'failed' ? (
                  <div className="flex items-center gap-2 bg-red-600/90 text-white rounded-lg px-3 py-2 text-[12px] font-semibold" data-testid="calendar-backup-failed-banner">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>{t('bookingCal.backupFailed2', 'CALENDAR BACKUP FAILED')} — {(() => { try { return format(new Date(backupStatus.day), 'd MMMM yyyy'); } catch { return backupStatus.day; } })()}</span>
                  </div>
                ) : (
                  <p className="text-[11px] text-white/80" data-testid="calendar-backup-verified">
                    {t('bookingCal.lastBackup', 'Last automatic backup')}: {(() => { try { return format(new Date(backupStatus.day), 'd MMMM yyyy'); } catch { return backupStatus.day; } })()} — <span className="font-semibold text-emerald-200">{t('bookingCal.verified', 'VERIFIED')}</span>
                  </p>
                )
              ) : (
                <p className="text-[11px] text-white/60" data-testid="calendar-backup-none">{t('bookingCal.noBackupYet', 'No automatic backup yet')}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 pb-24">
        {/* CALENDAR MODE */}
        {mode === 'calendar' && (
          <>
            <div className="bg-white rounded-xl border p-2 flex flex-col items-center max-w-sm mx-auto w-full" data-testid="calendar-month-grid">
              <Calendar
                mode="single"
                month={month}
                onMonthChange={setMonth}
                weekStartsOn={1}
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                components={{ DayContent }}
                locale={dfLocale()}
                dir={i18nInstance.dir()}
                formatters={calFormatters}
              />
            </div>
            <div className="flex items-center justify-between mt-4 mb-2 gap-2 flex-wrap">
              <div className="flex flex-col gap-1">
                <h2 className="text-sm font-bold text-gray-800" data-testid="calendar-selected-heading">{format(selectedDate, 'EEEE, d MMMM yyyy')}</h2>
                <HolidayChips dateStr={selStr} ctx={cardCtx} />
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {categories.length > 0 && (
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="h-8 w-36 text-[12px]" data-testid="calendar-category-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="catfilter-all">{t('bookingCal.allCategories', 'All categories')}</SelectItem>
                      <SelectItem value="__none__" data-testid="catfilter-none">{t('bookingCal.noCategory', 'No category')}</SelectItem>
                      {categories.map(c => <SelectItem key={c.id} value={c.id} data-testid={`catfilter-${c.id}`}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {canWrite && (
                  <Button size="sm" onClick={() => openCreate(selStr)} data-testid="calendar-add-booking">
                    <Plus className="h-4 w-4 mr-1" />{t('bookingCal.addBooking', 'Add booking')}
                  </Button>
                )}
                {canWrite && (
                  <Button size="sm" variant="outline" onClick={openAddBirthday} data-testid="calendar-add-birthday">
                    🎂 <span className="ml-1">{t('bookingCal.addBirthday', 'Add birthday')}</span>
                  </Button>
                )}
              </div>
            </div>
            <BirthdayChips dateStr={selStr} ctx={cardCtx} />
            {birthdaysForDate(selStr).length > 0 && (
              <div className="space-y-2 mb-2 mt-1" data-testid="calendar-day-birthdays">
                {birthdaysForDate(selStr).map(bd => (
                  <div key={bd.id} className="rounded-lg border border-pink-200 bg-pink-50 p-2.5 flex items-center justify-between gap-2" data-testid={`day-birthday-${bd.id}`}>
                    <span className="text-sm font-medium text-pink-800">🎂 {bd.personName}</span>
                    {canWrite && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px]" onClick={() => openEditBirthday(bd)} data-testid={`day-birthday-edit-${bd.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        {canDelete && <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px] text-red-600" onClick={() => setBdayToDelete(bd)} data-testid={`day-birthday-delete-${bd.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {dayList.length === 0 ? (
              <div className="text-center text-sm text-gray-500 py-8" data-testid="calendar-day-empty">{t('bookingCal.noBookingsDay', 'No bookings on this day.')}</div>
            ) : (
              <div className="space-y-2">{dayList.map(b => <BookingCard key={b.id} b={b} ctx={cardCtx} />)}</div>
            )}
            {/* Day navigation — bottom of day view */}
            <div className="mt-6 pt-4 border-t flex items-center justify-between gap-2" data-testid="calendar-day-nav">
              <button
                type="button"
                onClick={goPrevDay}
                className="flex items-center gap-1.5 h-12 px-4 rounded-xl border border-gray-200 bg-white text-gray-700 font-medium text-sm hover:bg-gray-50 active:bg-gray-100 transition-colors shadow-sm"
                data-testid="calendar-day-prev"
                aria-label={t('bookingCal.previousDay', 'Previous day')}
              >
                <ChevronLeft className="h-6 w-6 shrink-0" />
                <span>{t('bookingCal.previousDay', 'Previous day')}</span>
              </button>
              <button
                type="button"
                onClick={goTodayDayNav}
                className="h-12 px-5 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm"
                data-testid="calendar-day-today"
              >
                {t('bookingCal.today', 'Today')}
              </button>
              <button
                type="button"
                onClick={goNextDay}
                className="flex items-center gap-1.5 h-12 px-4 rounded-xl border border-gray-200 bg-white text-gray-700 font-medium text-sm hover:bg-gray-50 active:bg-gray-100 transition-colors shadow-sm"
                data-testid="calendar-day-next"
                aria-label={t('bookingCal.nextDay', 'Next day')}
              >
                <span>{t('bookingCal.nextDay', 'Next day')}</span>
                <ChevronRight className="h-6 w-6 shrink-0" />
              </button>
            </div>
          </>
        )}

        {/* AGENDA MODE */}
        {mode === 'agenda' && (
          <>
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex gap-1 flex-wrap" data-testid="agenda-filters">
                {[['today', t('bookingCal.today', 'Today')], ['upcoming', t('bookingCal.upcoming', 'Upcoming')], ['past', t('bookingCal.past', 'Past')], ['cancelled', t('bookingCal.cancelledFilter', 'Cancelled')]].map(([k, lbl]) => (
                  <Button key={k} size="sm" variant={agendaFilter === k ? 'default' : 'outline'} onClick={() => setAgendaFilter(k)} data-testid={`agenda-filter-${k}`}>{lbl}</Button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Select value={payFilter} onValueChange={setPayFilter}>
                  <SelectTrigger className="h-8 w-40 text-[12px]" data-testid="agenda-payment-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="payfilter-all">{t('bookingCal.payAll', 'All payments')}</SelectItem>
                    <SelectItem value="due" data-testid="payfilter-due">{t('bookingCal.payDue', 'Money due')}</SelectItem>
                    <SelectItem value="paid" data-testid="payfilter-paid">{t('bookingCal.payPaid', 'Paid')}</SelectItem>
                    <SelectItem value="free" data-testid="payfilter-free">{t('bookingCal.payFree', 'Free')}</SelectItem>
                  </SelectContent>
                </Select>
                {canWrite && <Button size="sm" onClick={() => openCreate()} data-testid="agenda-add-booking"><Plus className="h-4 w-4 mr-1" />{t('bookingCal.add', 'Add')}</Button>}
              </div>
            </div>
            {categories.length > 0 && (
              <div className="mb-3">
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="h-8 w-44 text-[12px]" data-testid="agenda-category-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="agenda-catfilter-all">{t('bookingCal.allCategories', 'All categories')}</SelectItem>
                    <SelectItem value="__none__" data-testid="agenda-catfilter-none">{t('bookingCal.noCategory', 'No category')}</SelectItem>
                    {categories.map(c => <SelectItem key={c.id} value={c.id} data-testid={`agenda-catfilter-${c.id}`}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {agendaGroups.length === 0 ? (
              <div className="text-center text-sm text-gray-500 py-10" data-testid="agenda-empty">{t('bookingCal.noBookings', 'No bookings.')}</div>
            ) : (
              <div className="space-y-4">{agendaGroups.map(([date, items]) => <DateGroup key={date} date={date} items={items} ctx={cardCtx} />)}</div>
            )}
            {/* Birthdays — yearly, shown as their own clearly-separated section */}
            <div className="mt-6" data-testid="agenda-birthdays">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-bold text-gray-700">🎂 {t('bookingCal.birthdays', 'Birthdays')}</h2>
                {canWrite && (
                  <Button size="sm" variant="outline" onClick={openAddBirthday} data-testid="agenda-add-birthday">
                    <Plus className="h-4 w-4 mr-1" />{t('bookingCal.addBirthday', 'Add birthday')}
                  </Button>
                )}
              </div>
              {birthdays.length === 0 ? (
                <div className="text-center text-sm text-gray-500 py-4" data-testid="agenda-birthdays-empty">{t('bookingCal.noBirthdays', 'No birthdays yet.')}</div>
              ) : (
                <div className="space-y-2">
                  {birthdays.map(bd => (
                    <div key={bd.id} className="rounded-lg border border-pink-200 bg-pink-50 p-2.5 flex items-center justify-between gap-2" data-testid={`agenda-birthday-${bd.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-pink-800 truncate">🎂 {bd.personName}</p>
                        <p className="text-[12px] text-pink-600">{bd.day} {monthName(bd.month)} · {t('bookingCal.repeatsYearly', 'Repeats every year')}{bd.reminder && bd.reminder !== 'none' ? ` · ${bdayReminderLabel(bd.reminder)}` : ''}</p>
                      </div>
                      {canWrite && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px]" onClick={() => openEditBirthday(bd)} data-testid={`agenda-birthday-edit-${bd.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />{t('bookingCal.edit', 'Edit')}</Button>
                          {canDelete && <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px] text-red-600" onClick={() => setBdayToDelete(bd)} data-testid={`agenda-birthday-delete-${bd.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />{t('bookingCal.delete', 'Delete')}</Button>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* SEARCH MODE */}
        {mode === 'search' && (
          <>
            <div className="relative mb-3">
              <SearchIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input className="pl-9" placeholder={t('bookingCal.searchPlaceholder', 'Search client name or service…')} value={query} onChange={e => setQuery(e.target.value)} data-testid="search-input" autoFocus />
            </div>
            {query.trim() === '' ? (
              <div className="text-center text-sm text-gray-500 py-10" data-testid="search-prompt">{t('bookingCal.searchPrompt', 'Type a client name, service or birthday to search past and future bookings.')}</div>
            ) : (searchGroups.length === 0 && searchBirthdays.length === 0) ? (
              <div className="text-center text-sm text-gray-500 py-10" data-testid="search-empty">{t('bookingCal.noResults', 'No bookings or birthdays match your search.')}</div>
            ) : (
              <div className="space-y-4" data-testid="search-results">
                {searchBirthdays.length > 0 && (
                  <div className="space-y-2" data-testid="search-birthday-results">
                    {searchBirthdays.map(bd => (
                      <div
                        key={bd.id}
                        role={canWrite ? 'button' : undefined}
                        onClick={canWrite ? () => openEditBirthday(bd) : undefined}
                        className={`rounded-lg border border-pink-200 bg-pink-50 p-2.5 flex items-center justify-between gap-2 ${canWrite ? 'cursor-pointer hover:bg-pink-100 transition-colors' : ''}`}
                        data-testid={`search-birthday-${bd.id}`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-pink-800 truncate flex items-center gap-1.5">
                            🎂 {bd.personName}
                            <span className="text-[9px] font-bold uppercase tracking-wide bg-pink-200 text-pink-800 rounded px-1.5 py-0.5" data-testid={`search-birthday-tag-${bd.id}`}>{t('bookingCal.birthday', 'Birthday')}</span>
                          </p>
                          <p className="text-[12px] text-pink-600">{bd.day} {monthName(bd.month)} · {t('bookingCal.repeatsYearly', 'Repeats every year')}</p>
                        </div>
                        {canWrite && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px] shrink-0" onClick={(e) => { e.stopPropagation(); openEditBirthday(bd); }} data-testid={`search-birthday-edit-${bd.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />{t('bookingCal.edit', 'Edit')}</Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {searchGroups.map(([date, items]) => <DateGroup key={date} date={date} items={items} ctx={cardCtx} />)}
              </div>
            )}
          </>
        )}

        {/* RECYCLE MODE */}
        {mode === 'recycle' && (
          <div className="space-y-2" data-testid="calendar-recycle-list">
            <p className="text-[12px] text-gray-500">{t('bookingCal.recycleHint', 'Deleted bookings are kept for 30 days and can be restored.')}</p>
            {recycle.length === 0 ? (
              <div className="text-center text-sm text-gray-500 py-10" data-testid="calendar-recycle-empty">{t('bookingCal.recycleEmpty', 'Recycle Bin is empty.')}</div>
            ) : recycle.map(b => (
              <div key={b.id} className="bg-white rounded-lg border p-3 flex items-start justify-between gap-2" data-testid={`recycle-${b.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{b.clientName} — {b.service}</p>
                  <p className="text-[12px] text-gray-500">{format(parseISO(b.date), 'EEE d MMM yyyy')} · {b.startTime}–{b.endTime}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{t('bookingCal.deletedBy', 'Deleted by')} {b.deletedByName || '—'}</p>
                </div>
                {canDeleteBooking(b) && (
                  <Button size="sm" variant="outline" onClick={() => doRestore(b)} data-testid={`recycle-restore-${b.id}`}>
                    <RotateCcw className="h-4 w-4 mr-1" />{t('bookingCal.restore', 'Restore')}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        {mode === 'lists' && calendar?.id && (
          <ListsPanel calendarId={calendar.id} role={role} />
        )}
        {mode === 'notes' && calendar?.id && (
          <NotesPanel calendarId={calendar.id} role={role} />
        )}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto" data-testid="booking-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t('bookingCal.editBooking', 'Edit booking') : t('bookingCal.newBooking', 'New booking')}</DialogTitle>
            <DialogDescription>{t('bookingCal.formHint', 'Enter the client and appointment details.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-[11px]">{t('bookingCal.date', 'Date')}</Label><DateSelect value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} displayFormat="dd/MM/yyyy" placeholder={t('bookingCal.pickDate', 'Pick a date')} data-testid="booking-input-date" /></div>
              <div><Label className="text-[11px]">{t('bookingCal.start', 'Start')}</Label><Input type="time" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))} data-testid="booking-input-start" /></div>
              <div><Label className="text-[11px]">{t('bookingCal.end', 'End')}</Label><Input type="time" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))} data-testid="booking-input-end" /></div>
            </div>
            {(
              <div className="border rounded-lg p-2" data-testid="repeat-section">
                <Label className="text-[11px] font-semibold flex items-center gap-1"><RotateCw className="h-3 w-3" />{t('bookingCal.repeat', 'Repeat')}{editing && <span className="text-gray-400 font-normal">· {t('bookingCal.repeatEditHint', 'change how this booking repeats')}</span>}</Label>
                {form.repeat !== 'none' && (
                  <p className="text-[11px] text-purple-600 mt-1" data-testid="repeat-summary-preview">{describeRecurrence(buildRecurrence())}</p>
                )}
                <Select value={form.repeat} onValueChange={v => {
                  if (v === 'individual') { setFormOpen(false); setMultiDateOpen(true); return; }
                  setForm(f => ({ ...f, repeat: v }));
                }}>
                  <SelectTrigger className="mt-1 h-8" data-testid="repeat-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('bookingCal.repeatNone', 'Does not repeat')}</SelectItem>
                    <SelectItem value="daily">{t('bookingCal.repeatDaily', 'Every day')}</SelectItem>
                    <SelectItem value="weekly">{t('bookingCal.repeatWeekly', 'Every week')}</SelectItem>
                    <SelectItem value="biweekly">{t('bookingCal.repeatBiweekly', 'Every 2 weeks')}</SelectItem>
                    <SelectItem value="monthly">{t('bookingCal.repeatMonthly', 'Every month')}</SelectItem>
                    <SelectItem value="custom">{t('bookingCal.repeatCustom', 'Custom')}</SelectItem>
                    {!editing && <SelectItem value="individual" data-testid="repeat-individual">{t('bookingCal.md_title', 'Select dates & edit individually')}</SelectItem>}
                  </SelectContent>
                </Select>
                {form.repeat === 'custom' && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[12px]">{t('bookingCal.every', 'Every')}</span>
                      <Input type="number" min="1" className="w-16 h-8" value={form.customInterval} onChange={e => setForm(f => ({ ...f, customInterval: e.target.value }))} data-testid="repeat-interval" />
                      <Select value={form.customFreq} onValueChange={v => setForm(f => ({ ...f, customFreq: v }))}>
                        <SelectTrigger className="h-8 w-28" data-testid="repeat-freq"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">{t('bookingCal.days', 'days')}</SelectItem>
                          <SelectItem value="weekly">{t('bookingCal.weeks', 'weeks')}</SelectItem>
                          <SelectItem value="monthly">{t('bookingCal.months', 'months')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {form.customFreq === 'weekly' && (
                      <div className="flex gap-1 flex-wrap" data-testid="repeat-weekdays">
                        {[['Mo', 0], ['Tu', 1], ['We', 2], ['Th', 3], ['Fr', 4], ['Sa', 5], ['Su', 6]].map(([lbl, idx]) => (
                          <button type="button" key={idx} onClick={() => setForm(f => ({ ...f, customWeekdays: f.customWeekdays.includes(idx) ? f.customWeekdays.filter(x => x !== idx) : [...f.customWeekdays, idx] }))}
                            className={`w-9 h-8 rounded text-[11px] border ${form.customWeekdays.includes(idx) ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600'}`} data-testid={`weekday-${idx}`}>{lbl}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {form.repeat !== 'none' && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="text-[12px]">{t('bookingCal.ends', 'Ends')}</span>
                    <Select value={form.endType} onValueChange={v => setForm(f => ({ ...f, endType: v }))}>
                      <SelectTrigger className="h-8 w-36" data-testid="repeat-endtype"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="count">{t('bookingCal.endAfter', 'After N times')}</SelectItem>
                        <SelectItem value="date">{t('bookingCal.endOn', 'On date')}</SelectItem>
                      </SelectContent>
                    </Select>
                    {form.endType === 'count'
                      ? <Input type="number" min="1" max="366" className="w-20 h-8" value={form.endCount} onChange={e => setForm(f => ({ ...f, endCount: e.target.value }))} data-testid="repeat-endcount" />
                      : <Input type="date" className="h-8" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} data-testid="repeat-enddate" />}
                  </div>
                )}
              </div>
            )}
            <div><Label className="text-[11px]">{t('bookingCal.clientName', 'Client name')}</Label><Input value={form.clientName} onChange={e => setForm(f => ({ ...f, clientName: e.target.value }))} data-testid="booking-input-client" /></div>
            <div><Label className="text-[11px]">{t('bookingCal.service', 'Service / appointment')}</Label><Input value={form.service} onChange={e => setForm(f => ({ ...f, service: e.target.value }))} data-testid="booking-input-service" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[11px]">{t('bookingCal.phone', 'Telephone')}</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="booking-input-phone" /></div>
              <div><Label className="text-[11px]">{t('bookingCal.email', 'Email')}</Label><Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="booking-input-email" /></div>
            </div>
            <div>
              <Label className="text-[11px]">{t('bookingCal.status', 'Status')}</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="booking-input-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s} data-testid={`booking-status-opt-${s}`}>{t(STATUS_LABEL_KEYS[s] || '', s)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {members.length > 0 && (
              <div>
                <Label className="text-[11px]">{t('bookingCal.md_assignee', 'Assigned to')}</Label>
                <Select value={form.assigneeUserId || 'none'} onValueChange={v => setForm(f => ({ ...f, assigneeUserId: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="booking-input-assignee"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="booking-assignee-opt-none">{t('bookingCal.md_unassigned', 'Unassigned')}</SelectItem>
                    {Array.from(new Map(members.map(m => [m.userId, m])).values()).map(m => (
                      <SelectItem key={m.userId} value={m.userId} data-testid={`booking-assignee-opt-${m.userId}`}>{m.userName || m.userEmail}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[11px]">{t('bookingCal.category', 'Category / colour')}</Label>
              <Select value={form.categoryId || 'none'} onValueChange={v => {
                if (v === '__add__') { setNewCatName(''); setManageCatOpen(true); return; }
                if (v === '__manage__') { setManageCatOpen(true); return; }
                setForm(f => ({ ...f, categoryId: v === 'none' ? '' : v }));
              }}>
                <SelectTrigger data-testid="booking-input-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="booking-category-opt-none">{t('bookingCal.noCategory', 'No category')}</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id} data-testid={`booking-category-opt-${c.id}`}>
                      <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />{c.name}</span>
                    </SelectItem>
                  ))}
                  {canDelete && (
                    <>
                      <div className="border-t my-1" />
                      <SelectItem value="__add__" data-testid="booking-category-add">
                        <span className="inline-flex items-center gap-2 text-purple-700 font-medium"><Plus className="h-3.5 w-3.5" />{t('bookingCal.addCategory', 'Add category')}</span>
                      </SelectItem>
                      <SelectItem value="__manage__" data-testid="booking-category-manage">
                        <span className="inline-flex items-center gap-2 text-gray-700 font-medium"><Palette className="h-3.5 w-3.5" />{t('bookingCal.manageCategories', 'Manage categories')}</span>
                      </SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">{t('bookingCal.notes', 'Notes')}</Label>
              <Textarea rows={3} ref={autoGrowNotes} value={form.notes} maxLength={NOTES_MAX} onChange={e => { setForm(f => ({ ...f, notes: e.target.value })); autoGrowNotes(e.target); }} className="resize-y min-h-[72px] max-h-[400px] overflow-y-auto" data-testid="booking-input-notes" />
              <p className={`text-[11px] text-right mt-0.5 ${(form.notes || '').length >= NOTES_MAX ? 'text-red-600 font-medium' : 'text-gray-400'}`} data-testid="booking-notes-counter">
                {(form.notes || '').length.toLocaleString()} / {NOTES_MAX.toLocaleString()}
              </p>
            </div>
            <div className="border rounded-lg p-2" data-testid="reminder-section">
              <Label className="text-[11px] font-semibold flex items-center gap-1"><CalendarClock className="h-3 w-3" />{t('bookingCal.reminders', 'Reminders')}</Label>
              <p className="text-[11px] text-gray-500 mt-0.5">{t('bookingCal.remindersHint', 'Notify calendar members before this booking. Optional — choose any that apply.')}</p>
              <div className="flex flex-col gap-1.5 mt-2">
                {REMINDER_OPTIONS.map(opt => {
                  const checked = (form.reminderOffsets || []).includes(opt.value);
                  return (
                    <label key={opt.value} className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid={`reminder-opt-${opt.value}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-purple-600"
                        checked={checked}
                        onChange={() => setForm(f => ({
                          ...f,
                          reminderOffsets: checked
                            ? (f.reminderOffsets || []).filter(x => x !== opt.value)
                            : [...(f.reminderOffsets || []), opt.value],
                        }))}
                        data-testid={`reminder-checkbox-${opt.value}`}
                      />
                      <span>{t(`bookingCal.${opt.key}`, opt.label)}</span>
                    </label>
                  );
                })}
              </div>
              {(form.reminderOffsets || []).length === 0 && (
                <p className="text-[11px] text-gray-400 mt-1.5" data-testid="reminder-none-hint">{t('bookingCal.remindNone', 'No reminder')}</p>
              )}
            </div>
            <div className="border rounded-lg p-2 space-y-2" data-testid="payment-section">
              <Label className="text-[11px] font-semibold">{t('bookingCal.payment', 'Payment')}</Label>
              <Select value={form.payStatus} onValueChange={v => setForm(f => ({ ...f, payStatus: v, _hasPayment: true }))}>
                <SelectTrigger className="h-8 w-full" data-testid="pay-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">{t('bookingCal.unpaid', 'Unpaid')}</SelectItem>
                  <SelectItem value="paid">{t('bookingCal.paidStatus', 'Paid')}</SelectItem>
                  <SelectItem value="free">{t('bookingCal.free', 'Free')}</SelectItem>
                </SelectContent>
              </Select>
              {form.payStatus !== 'free' && (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1"><span className="text-[12px] text-gray-500">€</span>
                      <Input type="number" step="0.01" min="0" className="w-24 h-8" placeholder={t('bookingCal.price', 'Price')} value={form.payPrice} onChange={e => setForm(f => ({ ...f, payPrice: e.target.value, _hasPayment: true }))} data-testid="pay-price" /></div>
                    <div className="flex items-center gap-1"><span className="text-[12px] text-gray-500">€</span>
                      <Input type="number" step="0.01" min="0" className="w-24 h-8" placeholder={t('bookingCal.amountPaid', 'Paid')} value={form.payStatus === 'paid' ? form.payPrice : form.payAmount} onChange={e => setForm(f => ({ ...f, payAmount: e.target.value, _hasPayment: true }))} data-testid="pay-amount" disabled={form.payStatus === 'paid'} /></div>
                    <span className="text-[12px] font-medium text-gray-700" data-testid="pay-balance">{t('bookingCal.balance', 'Balance')}: €{Math.max(0, ((parseFloat(form.payPrice) || 0) - (form.payStatus === 'paid' ? (parseFloat(form.payPrice) || 0) : (parseFloat(form.payAmount) || 0)))).toFixed(2)}</span>
                  </div>
                  <Select value={form.payMethod} onValueChange={v => setForm(f => ({ ...f, payMethod: v, _hasPayment: true }))}>
                    <SelectTrigger className="h-8 w-full" data-testid="pay-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_specified">{t('bookingCal.notSpecified', 'Not specified')}</SelectItem>
                      <SelectItem value="cash">{t('bookingCal.cash', 'Cash')}</SelectItem>
                      <SelectItem value="card">{t('bookingCal.card', 'Card')}</SelectItem>
                      <SelectItem value="bank_transfer">{t('bookingCal.bankTransfer', 'Bank Transfer')}</SelectItem>
                      <SelectItem value="other">{t('bookingCal.other', 'Other')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input className="h-8" placeholder={t('bookingCal.paymentNotePh', 'Payment note (e.g. €10 deposit)')} value={form.payNote} onChange={e => setForm(f => ({ ...f, payNote: e.target.value, _hasPayment: true }))} data-testid="pay-note" />
                </>
              )}
            </div>
            {editing && editing.seriesId && (
              <div className="border rounded-lg p-2 bg-gray-50" data-testid="edit-scope">
                <Label className="text-[11px] font-semibold">{t('bookingCal.applyTo', 'Apply changes to')}</Label>
                <div className="space-y-1 mt-1">
                  {['this', 'future', 'series'].map(s => (
                    <label key={s} className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid={`edit-scope-${s}`}>
                      <input type="radio" name="editscope" checked={editScope === s} onChange={() => setEditScope(s)} />
                      {t(`bookingCal.scope_${s}`, s)} <span className="text-gray-400 text-[11px]">({seriesCount(editing, s)})</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} data-testid="booking-form-cancel">{t('cancel', 'Cancel')}</Button>
            <Button onClick={saveForm} disabled={saving} data-testid="booking-form-save">{saving ? t('saving', 'Saving...') : t('save', 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate-group edit scope choice */}
      <Dialog open={dupEditOpen} onOpenChange={o => { if (!o) setDupEditOpen(false); }}>
        <DialogContent className="max-w-sm" data-testid="dup-edit-choice-dialog">
          <DialogHeader>
            <DialogTitle>{t('bookingCal.dupEditTitle', 'Apply changes to')}</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] text-gray-500">
            {t('bookingCal.dupEditDesc', 'This booking is part of a duplicated group of {n} bookings. How would you like to save your changes?')
              .replace('{n}', editing ? dupGroupBookings(editing).length : 0)}
          </p>
          <div className="space-y-2 mt-1">
            <Button variant="outline" className="w-full justify-start" disabled={saving}
              onClick={() => performSave('this')} data-testid="dup-edit-this">
              {t('bookingCal.dupEditThis', 'This booking only')}
            </Button>
            <Button className="w-full justify-start" disabled={saving}
              onClick={() => performSave('dupgroup')} data-testid="dup-edit-all">
              {t('bookingCal.dupEditAll', 'All duplicated bookings')}
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDupEditOpen(false)} disabled={saving} data-testid="dup-edit-cancel">
              {t('cancel', 'Cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Reschedule dialog */}
      <Dialog open={!!rescheduleFor} onOpenChange={o => !o && setRescheduleFor(null)}>
        <DialogContent className="max-w-sm" data-testid="reschedule-dialog">
          <DialogHeader><DialogTitle>{t('bookingCal.reschedule', 'Reschedule')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[11px]">{t('bookingCal.date', 'Date')}</Label><DateSelect value={reForm.date} onChange={v => setReForm(f => ({ ...f, date: v }))} placeholder={t('bookingCal.pickDate', 'Pick a date')} data-testid="reschedule-input-date" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[11px]">{t('bookingCal.start', 'Start')}</Label><TimeSelect value={reForm.startTime} onChange={v => setReForm(f => ({ ...f, startTime: v }))} data-testid="reschedule-input-start" /></div>
              <div><Label className="text-[11px]">{t('bookingCal.end', 'End')}</Label><TimeSelect value={reForm.endTime} onChange={v => setReForm(f => ({ ...f, endTime: v }))} data-testid="reschedule-input-end" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleFor(null)} data-testid="reschedule-cancel">{t('cancel', 'Cancel')}</Button>
            <Button onClick={doReschedule} data-testid="reschedule-save">{t('save', 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export to Excel — custom From/To date range */}
      <Dialog open={exportOpen} onOpenChange={o => { if (!exporting) setExportOpen(o); }}>
        <DialogContent className="max-w-sm" data-testid="calendar-export-dialog">
          <DialogHeader>
            <DialogTitle>{t('bookingCal.exportTitle', 'Export Calendar to Excel')}</DialogTitle>
            <DialogDescription>{t('bookingCal.exportHint', 'Choose any date range — past or future. The file is pulled directly from your saved bookings.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-[11px]">{t('bookingCal.exportFrom', 'FROM DATE')}</Label>
              <DateSelect value={exportFrom} onChange={setExportFrom} placeholder={t('bookingCal.pickDate', 'Pick a date')} data-testid="calendar-export-from" />
            </div>
            <div>
              <Label className="text-[11px]">{t('bookingCal.exportTo', 'TO DATE')}</Label>
              <DateSelect value={exportTo} onChange={setExportTo} placeholder={t('bookingCal.pickDate', 'Pick a date')} data-testid="calendar-export-to" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)} disabled={exporting} data-testid="calendar-export-cancel">{t('cancel', 'Cancel')}</Button>
            <Button onClick={runRangeExport} disabled={exporting} data-testid="calendar-export-download">
              <Download className="h-4 w-4 mr-1" />{exporting ? t('bookingCal.exporting', 'Preparing…') : t('bookingCal.exportDownload', 'Download Excel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Cancel confirm */}
      <AlertDialog open={!!confirmCancel} onOpenChange={o => !o && setConfirmCancel(null)}>
        <AlertDialogContent data-testid="cancel-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bookingCal.cancelBookingQ', 'Cancel this booking?')}</AlertDialogTitle>
            <AlertDialogDescription>{t('bookingCal.cancelBookingDesc', 'It will be marked as cancelled but remain in the calendar.')}</AlertDialogDescription>
          </AlertDialogHeader>
          {confirmCancel?.seriesId && (
            <div className="border rounded-lg p-2 bg-gray-50" data-testid="cancel-scope">
              <p className="text-[11px] font-semibold mb-1">{t('bookingCal.applyTo', 'Apply changes to')}</p>
              {['this', 'future', 'series'].map(s => (
                <label key={s} className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid={`cancel-scope-${s}`}>
                  <input type="radio" name="cancelscope" checked={cancelScope === s} onChange={() => setCancelScope(s)} />
                  {t(`bookingCal.scope_${s}`, s)} <span className="text-gray-400 text-[11px]">({seriesCount(confirmCancel, s)})</span>
                </label>
              ))}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-confirm-no">{t('bookingCal.keepIt', 'Keep it')}</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} data-testid="cancel-confirm-yes">{t('bookingCal.cancelBooking', 'Cancel booking')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={o => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bookingCal.deleteBookingQ', 'Delete this booking?')}</AlertDialogTitle>
            <AlertDialogDescription>{t('bookingCal.deleteBookingDesc', 'It will move to the Recycle Bin and can be restored within 30 days.')}</AlertDialogDescription>
          </AlertDialogHeader>
          {confirmDelete?.seriesId && (
            <div className="border rounded-lg p-2 bg-gray-50" data-testid="delete-scope">
              <p className="text-[11px] font-semibold mb-1">{t('bookingCal.applyTo', 'Apply changes to')}</p>
              {['this', 'future', 'series'].map(s => (
                <label key={s} className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid={`delete-scope-${s}`}>
                  <input type="radio" name="deletescope" checked={deleteScope === s} onChange={() => setDeleteScope(s)} />
                  {t(`bookingCal.scope_${s}`, s)} <span className="text-gray-400 text-[11px]">({seriesCount(confirmDelete, s)})</span>
                </label>
              ))}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-confirm-no">{t('cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-yes">{t('bookingCal.delete', 'Delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent delete confirm */}
      <AlertDialog open={!!confirmPurge} onOpenChange={o => !o && setConfirmPurge(null)}>
        <AlertDialogContent data-testid="purge-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">{t('bookingCal.purgeBookingQ', 'Permanently delete this appointment?')}</AlertDialogTitle>
            <AlertDialogDescription>{t('bookingCal.purgeBookingDesc', 'This cannot be undone — it will NOT go to the Recycle Bin. The appointment is removed from the calendar and lists and the time slot becomes free again. Its payment and history records are kept.')}</AlertDialogDescription>
          </AlertDialogHeader>
          {confirmPurge?.seriesId && (
            <div className="border rounded-lg p-2 bg-red-50" data-testid="purge-scope">
              <p className="text-[11px] font-semibold mb-1">{t('bookingCal.applyTo', 'Apply changes to')}</p>
              {['this', 'future', 'series'].map(s => (
                <label key={s} className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid={`purge-scope-${s}`}>
                  <input type="radio" name="purgescope" checked={purgeScope === s} onChange={() => setPurgeScope(s)} />
                  {t(`bookingCal.scope_${s}`, s)} <span className="text-gray-400 text-[11px]">({seriesCount(confirmPurge, s)})</span>
                </label>
              ))}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="purge-confirm-no">{t('cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={doPurge} className="bg-red-700 hover:bg-red-800" data-testid="purge-confirm-yes">{t('bookingCal.deletePermanently', 'Delete permanently')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Skip this date (recurring) */}
      <AlertDialog open={!!confirmSkip} onOpenChange={o => !o && setConfirmSkip(null)}>
        <AlertDialogContent data-testid="skip-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bookingCal.skipDateQ', 'Skip this date?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmSkip && t('bookingCal.skipDateDesc', 'Only {date} will be removed. The rest of the repeating series stays unchanged. You can restore it later from the Recycle Bin.')
                .replace('{date}', confirmSkip ? (() => { try { return new Date(confirmSkip.date + 'T00:00:00').toLocaleDateString(i18n.language || undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); } catch (e) { return confirmSkip.date; } })() : '')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="skip-confirm-no">{t('cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={doSkip} className="bg-amber-600 hover:bg-amber-700" data-testid="skip-confirm-yes">{t('bookingCal.skipDate', 'Skip this date')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate-group delete: this / selected / all */}
      <Dialog open={!!dupDelFor} onOpenChange={(o) => { if (!dupDelBusy && !o) setDupDelFor(null); }}>
        <DialogContent className="max-w-md" data-testid="dup-delete-dialog">
          <DialogHeader>
            <DialogTitle>{t('bookingCal.dupDelTitle', 'Delete duplicated booking')}</DialogTitle>
            <DialogDescription>
              {t('bookingCal.dupDelIntro', 'This booking is one of {n} created together by Duplicate.').replace('{n}', dupGroupBookings(dupDelFor).length)}
            </DialogDescription>
          </DialogHeader>
          {dupDelMode === 'menu' && (
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start h-11" onClick={dupDeleteThisOnly} disabled={dupDelBusy} data-testid="dup-del-this">
                <Trash2 className="h-4 w-4 mr-2" />{t('bookingCal.dupDelThis', 'Delete this booking only')}
              </Button>
              <Button variant="outline" className="w-full justify-start h-11" onClick={() => setDupDelMode('select')} disabled={dupDelBusy} data-testid="dup-del-select-open">
                <ListChecks className="h-4 w-4 mr-2" />{t('bookingCal.dupDelSelect', 'Select bookings to delete')}
              </Button>
              <Button variant="outline" className="w-full justify-start h-11 text-red-600 border-red-300 hover:bg-red-50" onClick={() => setDupDelMode('confirmAll')} disabled={dupDelBusy} data-testid="dup-del-all-open">
                <Trash2 className="h-4 w-4 mr-2" />{t('bookingCal.dupDelAll', 'Delete all duplicated bookings')} ({dupGroupBookings(dupDelFor).length})
              </Button>
              <DialogFooter className="pt-1">
                <Button variant="ghost" onClick={() => setDupDelFor(null)} disabled={dupDelBusy} data-testid="dup-del-cancel">{t('cancel', 'Cancel')}</Button>
              </DialogFooter>
            </div>
          )}
          {dupDelMode === 'select' && (
            <div className="space-y-2">
              <div className="max-h-64 overflow-y-auto space-y-1.5" data-testid="dup-del-list">
                {dupGroupBookings(dupDelFor).map(g => (
                  <label key={g.id} className="flex items-center gap-2 border rounded-lg p-2 text-[13px] cursor-pointer" data-testid={`dup-del-item-${g.id}`}>
                    <input type="checkbox" className="h-4 w-4 accent-purple-600" checked={dupDelSel.has(g.id)}
                      onChange={() => setDupDelSel(s => { const n = new Set(s); n.has(g.id) ? n.delete(g.id) : n.add(g.id); return n; })}
                      data-testid={`dup-del-check-${g.id}`} />
                    <span className="flex-1">{format(parseISO(g.date), 'EEE d MMM yyyy')} · {g.startTime}–{g.endTime}
                      {g.id === dupDelFor?.id && <span className="text-purple-600"> ({t('bookingCal.dupThis', 'this one')})</span>}</span>
                  </label>
                ))}
              </div>
              <div className="flex justify-between gap-2">
                <Button variant="ghost" onClick={() => setDupDelMode('menu')} disabled={dupDelBusy} data-testid="dup-del-back">{t('back', 'Back')}</Button>
                <Button className="bg-red-600 hover:bg-red-700" onClick={dupDeleteSelected} disabled={dupDelBusy || dupDelSel.size === 0} data-testid="dup-del-selected-go">
                  {t('bookingCal.dupDelSelectedGo', 'Delete selected')} ({dupDelSel.size})
                </Button>
              </div>
            </div>
          )}
          {dupDelMode === 'confirmAll' && (
            <div className="space-y-3">
              <p className="text-[13px]">{t('bookingCal.dupDelAllConfirm', 'Delete all {n} duplicated bookings? They will move to the Recycle Bin.').replace('{n}', dupGroupBookings(dupDelFor).length)}</p>
              <div className="flex justify-between gap-2">
                <Button variant="ghost" onClick={() => setDupDelMode('menu')} disabled={dupDelBusy} data-testid="dup-del-all-back">{t('back', 'Back')}</Button>
                <Button className="bg-red-600 hover:bg-red-700" onClick={dupDeleteAll} disabled={dupDelBusy} data-testid="dup-del-all-go">{t('bookingCal.dupDelAllGo', 'Delete all')}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage Categories dialog */}
      <Dialog open={manageCatOpen} onOpenChange={setManageCatOpen}>
        <DialogContent className="max-w-md" data-testid="manage-categories-dialog">
          <DialogHeader>
            <DialogTitle>{t('bookingCal.manageCategories', 'Manage categories')}</DialogTitle>
            <DialogDescription>{t('bookingCal.manageCatHint', 'Shared colour categories for this calendar.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Add new */}
            <div className="border rounded-lg p-2 space-y-2">
              <Input placeholder={t('bookingCal.newCatName', 'New category name')} value={newCatName} onChange={e => setNewCatName(e.target.value)} data-testid="new-category-name" />
              <div className="flex flex-wrap gap-1.5" data-testid="new-category-palette">
                {PALETTE.map(c => {
                  const used = usedColors.has(c);
                  return (
                  <button key={c} onClick={() => setNewCatColor(c)} data-testid={`palette-${c}`}
                    data-used={used ? 'true' : 'false'}
                    title={used ? t('bookingCal.colourInUse', 'Already used by another category') : ''}
                    className={`w-6 h-6 rounded-full flex items-center justify-center relative ${newCatColor === c ? 'ring-2 ring-offset-1 ring-gray-800' : ''}`}
                    style={{ backgroundColor: c }}>
                    {newCatColor === c && <Check className="h-3.5 w-3.5 text-white" />}
                    {used && newCatColor !== c && <span className="w-1.5 h-1.5 bg-white rounded-full absolute ring-1 ring-black/25" />}
                  </button>
                );})}
              </div>
              <Button size="sm" className="w-full" onClick={addCategory} data-testid="add-category-btn"><Plus className="h-4 w-4 mr-1" />{t('bookingCal.addCategory', 'Add category')}</Button>
            </div>
            {/* Existing */}
            <div className="max-h-64 overflow-y-auto space-y-2" data-testid="category-list">
              {categories.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-3">{t('bookingCal.noCategories', 'No categories yet.')}</p>
              ) : categories.map(cat => (
                <div key={cat.id} className="border rounded-lg p-2" data-testid={`category-row-${cat.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                    <Input defaultValue={cat.name} className="h-8 text-[13px]" onBlur={e => renameCategory(cat, e.target.value)} data-testid={`category-name-${cat.id}`} />
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 shrink-0" onClick={() => setCatToDelete(cat)} data-testid={`category-delete-${cat.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {PALETTE.map(c => {
                      const usedByOther = usedColors.has(c) && c !== cat.color;
                      return (
                      <button key={c} onClick={() => recolorCategory(cat, c)} data-testid={`category-color-${cat.id}-${c}`}
                        data-used={usedByOther ? 'true' : 'false'}
                        title={usedByOther ? t('bookingCal.colourInUse', 'Already used by another category') : ''}
                        className={`w-5 h-5 rounded-full flex items-center justify-center relative ${cat.color === c ? 'ring-2 ring-offset-1 ring-gray-700' : ''}`}
                        style={{ backgroundColor: c }}>
                        {cat.color === c && <Check className="h-3 w-3 text-white" />}
                        {usedByOther && <span className="w-1.5 h-1.5 bg-white rounded-full absolute ring-1 ring-black/25" />}
                      </button>
                    );})}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManageCatOpen(false)} data-testid="manage-categories-close">{t('bookingCal.close', 'Close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category safe-delete confirm */}
      <AlertDialog open={!!catToDelete} onOpenChange={o => !o && setCatToDelete(null)}>
        <AlertDialogContent data-testid="category-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bookingCal.deleteCatQ', 'Delete this category?')}</AlertDialogTitle>
            <AlertDialogDescription>{t('bookingCal.deleteCatDesc', 'Bookings using it will NOT be deleted — they will simply lose this category.')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="category-delete-cancel">{t('cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={deleteCategory} className="bg-red-600 hover:bg-red-700" data-testid="category-delete-confirm">{t('bookingCal.delete', 'Delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Audit history dialog */}
      <Dialog open={!!auditFor} onOpenChange={o => !o && setAuditFor(null)}>
        <DialogContent className="max-w-md" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle>{t('bookingCal.history', 'History')}</DialogTitle>
            <DialogDescription>{auditFor?.clientName}</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {auditRows.length === 0 ? <p className="text-sm text-gray-500">{t('bookingCal.noHistory', 'No history yet.')}</p> :
              auditRows.map(a => (
                <div key={a.id} className="text-[12px] border-b pb-2" data-testid={`audit-row-${a.action}`}>
                  <div className="flex justify-between">
                    <span className="font-semibold">{a.action}</span>
                    <span className="text-gray-400">{format(parseISO(a.at), 'd MMM yyyy HH:mm')}</span>
                  </div>
                  <p className="text-gray-500">{t('bookingCal.by', 'by')} {a.actorName}</p>
                  {a.action === 'RESCHEDULED' && a.before && a.after && (
                    <p className="text-gray-500">{a.before.date} {a.before.startTime} → {a.after.date} {a.after.startTime}</p>
                  )}
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create calendar dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-sm" data-testid="create-calendar-dialog">
          <DialogHeader>
            <DialogTitle>{t('bookingCal.createCalendar', 'Create calendar')}</DialogTitle>
            <DialogDescription>{t('bookingCal.createCalHint', 'Your new calendar is private. You become its owner.')}</DialogDescription>
          </DialogHeader>
          <Input autoFocus placeholder={t('bookingCal.calNamePlaceholder', 'e.g. Personal, Work, Family')} value={newCalName} onChange={e => setNewCalName(e.target.value)} data-testid="create-calendar-name" />
          <div className="mt-2">
            <Label className="text-[11px]">{t('bookingCal.municipality', 'Municipality (optional)')}</Label>
            <select value={newCalMunicipality} onChange={e => setNewCalMunicipality(e.target.value)} className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white mt-1" data-testid="create-calendar-municipality">
              <option value="">{t('bookingCal.municipalityNone', 'None — national & Andalucía holidays only')}</option>
              {townOptions.map(tn => <option key={tn} value={tn}>{tn}</option>)}
            </select>
            <p className="text-[11px] text-gray-400 mt-1">{t('bookingCal.municipalityHint', 'Local holidays for this town will show on the calendar.')}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="create-calendar-cancel">{t('cancel', 'Cancel')}</Button>
            <Button onClick={createCalendar} data-testid="create-calendar-save">{t('bookingCal.createCalendar', 'Create calendar')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename calendar dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-sm" data-testid="rename-calendar-dialog">
          <DialogHeader><DialogTitle>{t('bookingCal.calendarSettings', 'Calendar settings')}</DialogTitle></DialogHeader>
          <Label className="text-[11px]">{t('bookingCal.calName', 'Calendar name')}</Label>
          <Input autoFocus value={renameVal} onChange={e => setRenameVal(e.target.value)} data-testid="rename-calendar-input" />
          <div className="mt-1">
            <Label className="text-[11px]">{t('bookingCal.municipality', 'Municipality (optional)')}</Label>
            <select value={renameMunicipality} onChange={e => setRenameMunicipality(e.target.value)} className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white mt-1" data-testid="rename-calendar-municipality">
              <option value="">{t('bookingCal.municipalityNone', 'None — national & Andalucía holidays only')}</option>
              {townOptions.map(tn => <option key={tn} value={tn}>{tn}</option>)}
            </select>
            <p className="text-[11px] text-gray-400 mt-1">{t('bookingCal.municipalityHint', 'Local holidays for this town will show on the calendar.')}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)} data-testid="rename-calendar-cancel">{t('cancel', 'Cancel')}</Button>
            <Button onClick={renameCalendar} data-testid="rename-calendar-save">{t('save', 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete calendar confirm */}
      <AlertDialog open={confirmDeleteCal} onOpenChange={setConfirmDeleteCal}>
        <AlertDialogContent data-testid="delete-calendar-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bookingCal.deleteCalendarQ', 'Delete this calendar?')}</AlertDialogTitle>
            <AlertDialogDescription>{t('bookingCal.deleteCalendarDesc', 'The calendar and its bookings will be hidden and can be restored within 30 days.')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-calendar-cancel">{t('cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={deleteCalendarFn} className="bg-red-600 hover:bg-red-700" data-testid="delete-calendar-confirm">{t('bookingCal.deleteCalendar', 'Delete calendar')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Members dialog */}
      <Dialog open={membersOpen} onOpenChange={setMembersOpen}>
        <DialogContent className="max-w-md" data-testid="members-dialog">
          <DialogHeader>
            <DialogTitle>{t('bookingCal.members', 'Members')}</DialogTitle>
            <DialogDescription>{t('bookingCal.membersHint', 'People who can access this calendar and their roles.')}</DialogDescription>
          </DialogHeader>
          {/* Invite */}
          <div className="border rounded-lg p-2 space-y-2" data-testid="members-invite">
            <Label className="text-[11px]">{t('bookingCal.inviteByEmail', 'Invite by email')}</Label>
            <div className="flex gap-2">
              <Input placeholder="name@email.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} data-testid="invite-email" />
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="w-32" data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {isOwner && <SelectItem value="admin" data-testid="invite-role-admin">{ROLE_LABELS.admin}</SelectItem>}
                  <SelectItem value="member" data-testid="invite-role-member">{ROLE_LABELS.member}</SelectItem>
                  <SelectItem value="view_only" data-testid="invite-role-view_only">{ROLE_LABELS.view_only}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" className="w-full" onClick={inviteMember} disabled={busyMembers} data-testid="invite-submit">
              <UserPlus className="h-4 w-4 mr-1" />{t('bookingCal.sendInvite', 'Invite')}
            </Button>
          </div>
          {/* Member list */}
          <div className="max-h-72 overflow-y-auto space-y-2" data-testid="members-list">
            {members.map(m => {
              const isMe = m.userId === (user?.userId || user?.id);
              const canEditRole = isOwner || (role === 'admin' && ['member', 'view_only'].includes(m.role));
              const canRemove = !isMe && m.role !== 'owner' && (isOwner || role === 'admin');
              return (
                <div key={m.userId} className="border rounded-lg p-2 flex flex-wrap items-center gap-2" data-testid={`member-row-${m.userId}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-medium truncate flex items-center gap-1">
                      {m.role === 'owner' && <Crown className="h-3.5 w-3.5 text-amber-500" />}
                      {m.role === 'admin' && <Shield className="h-3.5 w-3.5 text-purple-500" />}
                      {m.userName || m.userEmail}
                    </p>
                    <p className="text-[11px] text-gray-400 truncate">{m.userEmail}</p>
                  </div>
                  {canEditRole && m.role !== 'owner' && !isMe ? (
                    <Select value={m.role} onValueChange={v => changeMemberRole(m, v)}>
                      <SelectTrigger className="w-28 h-8 text-[12px]" data-testid={`member-role-${m.userId}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {isOwner && <SelectItem value="admin">{ROLE_LABELS.admin}</SelectItem>}
                        <SelectItem value="member">{ROLE_LABELS.member}</SelectItem>
                        <SelectItem value="view_only">{ROLE_LABELS.view_only}</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="secondary" className="text-[10px]" data-testid={`member-badge-${m.userId}`}>{ROLE_LABELS[m.role]}</Badge>
                  )}
                  {isOwner && m.role !== 'owner' && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-600 shrink-0" title={t('bookingCal.makeOwner', 'Make owner')} onClick={() => transferOwnership(m)} data-testid={`member-make-owner-${m.userId}`}>
                      <Crown className="h-4 w-4" />
                    </Button>
                  )}
                  {canRemove && (
                    <Button size="sm" variant="outline" className="h-8 px-2.5 border-red-300 text-red-600 hover:bg-red-50 shrink-0" title={t('bookingCal.remove', 'Remove')} onClick={() => setConfirmRemoveMember(m)} data-testid={`member-remove-${m.userId}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" />{t('bookingCal.remove', 'Remove')}
                    </Button>
                  )}
                </div>
              );
            })}
            {pendingInvites.length > 0 && (
              <div className="pt-1">
                <p className="text-[11px] font-semibold text-gray-500 mb-1">{t('bookingCal.pendingInvites', 'Pending invites')}</p>
                {pendingInvites.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-[12px] text-gray-500 border rounded-lg px-2 py-1.5" data-testid={`pending-invite-${i}`}>
                    <span className="truncate">{p.email}</span>
                    <Badge variant="outline" className="text-[10px]">{ROLE_LABELS[p.role]}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMembersOpen(false)} data-testid="members-close">{t('bookingCal.close', 'Close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm member/viewer removal */}
      <AlertDialog open={!!confirmRemoveMember} onOpenChange={(o) => { if (!o) setConfirmRemoveMember(null); }}>
        <AlertDialogContent data-testid="member-remove-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('bookingCal.removeMemberTitleName', 'Remove {name} from this calendar?')
                .replace('{name}', confirmRemoveMember?.userName || confirmRemoveMember?.userEmail || t('bookingCal.thisPerson', 'this person'))}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('bookingCal.removeMemberBody2', 'They will immediately lose access to this calendar. Their existing bookings will NOT be deleted.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="member-remove-cancel">{t('cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => removeMember(confirmRemoveMember)} data-testid="member-remove-confirm">
              {t('bookingCal.remove', 'Remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate booking dialog — creates independent copies (never a series) */}
      <Dialog open={dupOpen} onOpenChange={(o) => { if (!dupBusy) setDupOpen(o); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="duplicate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Copy className="h-4 w-4" />{t('bookingCal.duplicateBooking', 'Duplicate booking')}</DialogTitle>
            <DialogDescription>{t('bookingCal.dupHint', 'Pick one or more dates. Each becomes a separate, fully independent booking (not a repeating series).')}</DialogDescription>
          </DialogHeader>
          {dupSource && (
            <div className="space-y-3">
              <div className="rounded-lg bg-slate-50 border p-2.5 text-[12px]" data-testid="duplicate-source">
                <p className="font-semibold text-gray-800 flex items-center gap-1"><User className="h-3.5 w-3.5 text-gray-400" />{dupSource.clientName}</p>
                {dupSource.service && <p className="text-gray-500">{dupSource.service}</p>}
                <p className="text-gray-500 mt-0.5">{t('bookingCal.dupOriginal', 'Original')}: {format(parseISO(dupSource.date), 'd MMM yyyy')} · {dupSource.startTime}–{dupSource.endTime}</p>
                <p className="text-gray-400 mt-0.5">{t('bookingCal.dupCopies', 'Copies: client, service, phone/email, status, category, notes & payment.')}</p>
              </div>
              <div className="flex gap-2" data-testid="duplicate-mode-toggle">
                <Button type="button" variant={dupMode === 'pick' ? 'default' : 'outline'} className="flex-1 h-9 text-[12px]" onClick={() => setDupMode('pick')} data-testid="dup-mode-pick">
                  {t('bookingCal.dupModePick', 'Pick individual dates')}
                </Button>
                <Button type="button" variant={dupMode === 'range' ? 'default' : 'outline'} className="flex-1 h-9 text-[12px]" onClick={() => setDupMode('range')} data-testid="dup-mode-range">
                  {t('bookingCal.dupModeEveryDay', 'Every day')}
                </Button>
              </div>
              {dupMode === 'pick' && (
                <div>
                  <Label className="text-[11px] font-semibold">{t('bookingCal.dupSelectDates', 'Select new date(s)')}</Label>
                  <div className="border rounded-lg mt-1 flex justify-center">
                    <Calendar
                      mode="multiple"
                      selected={dupDates}
                      onSelect={(days) => setDupDates(days || [])}
                      weekStartsOn={1}
                      locale={dfLocale()}
                      dir={i18nInstance.dir()}
                      formatters={calFormatters}
                      data-testid="duplicate-calendar"
                    />
                  </div>
                </div>
              )}
              {dupMode === 'range' && (
                <div className="grid grid-cols-2 gap-2" data-testid="duplicate-range">
                  <div>
                    <Label className="text-[11px]">{t('bookingCal.dupFrom', 'From')}</Label>
                    <Input type="date" value={dupRangeFrom} onChange={e => setDupRangeFrom(e.target.value)} className="h-9" data-testid="dup-range-from" />
                  </div>
                  <div>
                    <Label className="text-[11px]">{t('bookingCal.dupTo', 'To')}</Label>
                    <Input type="date" value={dupRangeTo} onChange={e => setDupRangeTo(e.target.value)} className="h-9" data-testid="dup-range-to" />
                  </div>
                  <p className="col-span-2 text-[11px] text-gray-500">{t('bookingCal.dupEveryDayHint', 'A booking will be created for every day in this range (inclusive).')}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">{t('bookingCal.startTime', 'Start')}</Label>
                  <Input type="time" value={dupStart} onChange={e => setDupStart(e.target.value)} className="h-9" data-testid="duplicate-start" />
                </div>
                <div>
                  <Label className="text-[11px]">{t('bookingCal.endTime', 'End')}</Label>
                  <Input type="time" value={dupEnd} onChange={e => setDupEnd(e.target.value)} className="h-9" data-testid="duplicate-end" />
                </div>
              </div>
              {Array.isArray(dupSource.reminders) && dupSource.reminders.length > 0 && (
                <label className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid="duplicate-copy-reminders-label">
                  <input type="checkbox" className="h-4 w-4 accent-purple-600" checked={dupCopyReminders} onChange={() => setDupCopyReminders(v => !v)} data-testid="duplicate-copy-reminders" />
                  <span>{t('bookingCal.dupCopyReminders', 'Also copy reminders ({n})').replace('{n}', dupSource.reminders.length)}</span>
                </label>
              )}
              <p className="text-[12px] font-medium text-gray-700 bg-blue-50 border border-blue-100 rounded-md p-2" data-testid="duplicate-count">
                {getDupDates().length === 0
                  ? t('bookingCal.dupNoDates', 'No dates selected yet.')
                  : t('bookingCal.dupWillCreate', 'This will create {n} independent booking(s).').replace('{n}', getDupDates().length)}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupOpen(false)} disabled={dupBusy} data-testid="duplicate-cancel">{t('cancel', 'Cancel')}</Button>
            <Button onClick={doDuplicate} disabled={dupBusy || getDupDates().length === 0} data-testid="duplicate-create">
              {dupBusy ? t('bookingCal.dupCreating', 'Creating...') : t('bookingCal.dupCreate', 'Duplicate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / Edit birthday dialog */}
      <Dialog open={bdayOpen} onOpenChange={setBdayOpen}>
        <DialogContent className="max-w-sm" data-testid="birthday-dialog">
          <DialogHeader>
            <DialogTitle>{bdayEditing ? t('bookingCal.editBirthday', 'Edit birthday') : t('bookingCal.addBirthday', 'Add birthday')}</DialogTitle>
            <DialogDescription>{t('bookingCal.birthdayHint', 'Birthdays repeat every year until you delete them. They are separate from bookings.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px]">{t('bookingCal.personName', 'Name')}</Label>
              <Input autoFocus value={bdayForm.personName} onChange={e => setBdayForm(f => ({ ...f, personName: e.target.value }))} placeholder={t('bookingCal.personNamePlaceholder', 'e.g. Adele')} data-testid="birthday-input-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px]">{t('bookingCal.day', 'Day')}</Label>
                <Select value={bdayForm.day} onValueChange={v => setBdayForm(f => ({ ...f, day: v }))}>
                  <SelectTrigger className="h-9" data-testid="birthday-input-day"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px]">{t('bookingCal.month', 'Month')}</Label>
                <Select value={bdayForm.month} onValueChange={v => setBdayForm(f => ({ ...f, month: v }))}>
                  <SelectTrigger className="h-9" data-testid="birthday-input-month"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <SelectItem key={m} value={String(m)}>{monthName(m)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[11px]">{t('bookingCal.reminder', 'Reminder')}</Label>
              <Select value={bdayForm.reminder} onValueChange={v => setBdayForm(f => ({ ...f, reminder: v }))}>
                <SelectTrigger className="h-9" data-testid="birthday-input-reminder"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BDAY_REMINDER_OPTS.map(r => <SelectItem key={r} value={r} data-testid={`birthday-reminder-opt-${r}`}>{bdayReminderLabel(r)}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-gray-500 mt-1">{t('bookingCal.birthdayReminderHint', 'Reminders notify calendar members at 09:00 (Spain time).')}</p>
            </div>
            {parseInt(bdayForm.day, 10) === 29 && parseInt(bdayForm.month, 10) === 2 && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="birthday-feb29-note">
                {t('bookingCal.feb29Note', 'In non-leap years this birthday is shown and reminded on 28 February.')}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBdayOpen(false)} data-testid="birthday-cancel">{t('cancel', 'Cancel')}</Button>
            <Button onClick={saveBirthday} data-testid="birthday-save">{t('save', 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete birthday confirm */}
      <AlertDialog open={!!bdayToDelete} onOpenChange={(o) => { if (!o) setBdayToDelete(null); }}>
        <AlertDialogContent data-testid="birthday-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bookingCal.deleteBirthdayTitle', 'Delete this birthday?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bookingCal.deleteBirthdayBody', 'This will remove {name} and stop all future yearly reminders.').replace('{name}', bdayToDelete?.personName || '')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="birthday-delete-cancel">{t('cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={deleteBirthday} data-testid="birthday-delete-confirm">{t('bookingCal.delete', 'Delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {calendar && (
        <MultiDateBooking
          open={multiDateOpen}
          onOpenChange={setMultiDateOpen}
          calendar={calendar}
          members={members}
          existingBookings={bookings}
          currentUserId={user?.userId || user?.id}
          onCreated={() => loadBookings(calendar.id)}
        />
      )}
    </div>
  );
};

export default CalendarPage;
